// src/pages/supervisor/SupervisorQuoteOperations.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../AuthContext';
import styles from './SupervisorQuoteOperations.module.css';
import QuoteSalesFlow from '../../components/QuoteSalesFlow/QuoteSalesFlow';
import {
  STALE_AFTER_MS as SHARED_STALE_AFTER_MS,
  ATTENTION_CATEGORIES,
  getQuoteDisplayStatus as sharedGetQuoteDisplayStatus,
  inferQuoteBusinessType as sharedInferQuoteBusinessType,
  inferExistingClientReason as sharedInferExistingClientReason,
  secondYesConfirmed as sharedSecondYesConfirmed,
  getThreeYesStage as sharedGetThreeYesStage,
  isGroupClosed as sharedIsGroupClosed,
  isGroupFollowUp as sharedIsGroupFollowUp,
  isGroupLive as sharedIsGroupLive,
  isGroupRegularQuote as sharedIsGroupRegularQuote,
  getGroupAttentionItem as sharedGetGroupAttentionItem,
} from '../../utils/quoteClassification';

const STALE_AFTER_MS = SHARED_STALE_AFTER_MS;

const NOT_CLOSED_REASONS = [
  ['lost_deal', 'Lost Deal'],
  ['walk', 'Walk'],
  ['did_not_rewrite_current_carrier', 'Did Not RW - Stayed With Current Carrier'],
];

const LEGACY_NOT_CLOSED_REASONS = [
  ['driving_record_price_increase', 'Driving Record Increased Price'],
  ['underwriting_carrier_decline', 'Underwriting / Carrier Will Not Accept'],
  ['down_payment_over_budget', 'Down Payment Higher Than Customer Has'],
  ['cannot_beat_current_monthly', 'Cannot Beat Current Monthly Payment'],
  ['overall_price_too_high', 'Overall Price Too High'],
  ['price_changed_after_rating', 'Price Changed After Rating / Bridging'],
  ['coverage_price_too_high', 'Coverage Needed Makes Price Too High'],
  ['no_competitive_market', 'No Competitive Carrier / Market Available'],
  ['customer_shopping', 'Customer Wants to Shop / Compare'],
  ['needs_time_decision_maker', 'Customer Needs More Time / Decision Maker'],
  ['no_better_price', 'No Better Price'],
  ['customer_declined', 'Customer Declined'],
  ['existing_policy_retained', 'Existing Policy / Endorsement Was Better'],
  ['not_eligible', 'Uninsurable / Not Eligible'],
  ['other', 'Other'],
];

const ALL_NOT_CLOSED_REASONS = [...NOT_CLOSED_REASONS, ...LEGACY_NOT_CLOSED_REASONS];

const QUOTE_BUSINESS_TYPES = [
  ['new_business', 'New Quote'],
  ['existing_client', 'Existing Client / Re-Write'],
];

const EXISTING_CLIENT_REASONS = [
  ['rewrite_endorsement', 'Endorsement Comparison'],
  ['renewal', 'Renewal'],
  ['reinstatement', 'Reinstatement'],
  ['rewrite_recent_policy', 'Re-Write - Policy Within Last 6 Months'],
  ['rewrite_payment', 'Payment'],
];

const PAYMENT_METHODS = [
  ['cash', 'Cash'],
  ['card', 'Card'],
  ['neither', 'Not Ready to Pay'],
];

const TABS = [
  ['live', 'Live Activity'],
  ['attention', 'Needs Attention'],
  ['quotes', 'Quotes'],
  ['followups', 'Follow-Ups'],
  ['calendar', 'Calendar'],
  ['closed', 'Closed Deals'],
  ['office_access', 'Office Log Access'],
];


function cleanStr(value) {
  return String(value ?? '').replace(/\r/g, '').trim();
}

function normalizePersonName(value) {
  return cleanStr(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeOffice(officeRaw = '') {
  const match = String(officeRaw || '').match(/CA\d{3}/i);
  return match ? match[0].toUpperCase() : cleanStr(officeRaw) || 'Unknown';
}

function getOfficeNumber(office = '') {
  const match = String(office).match(/CA(\d{3})/i);
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
}

function monthValue(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function monthBounds(value) {
  const [year, month] = value.split('-').map(Number);
  const start = new Date(year, month - 1, 1, 0, 0, 0, 0);
  const end = new Date(year, month, 1, 0, 0, 0, 0);
  return { start: start.toISOString(), end: end.toISOString() };
}

function dateValue(date = new Date()) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseLocalDate(value) {
  const [year, month, day] = String(value || '').split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1, 12, 0, 0, 0);
}

function rangeBounds(mode, selectedDate, selectedMonth) {
  if (mode === 'month') return monthBounds(selectedMonth);

  const anchor = parseLocalDate(selectedDate);
  const start = new Date(anchor);
  start.setHours(0, 0, 0, 0);

  if (mode === 'week') {
    const mondayOffset = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - mondayOffset);
  }

  const end = new Date(start);
  end.setDate(end.getDate() + (mode === 'week' ? 7 : 1));
  return { start: start.toISOString(), end: end.toISOString() };
}

function shiftRange(mode, selectedDate, selectedMonth, direction) {
  if (mode === 'month') {
    const [year, month] = selectedMonth.split('-').map(Number);
    return { month: monthValue(new Date(year, month - 1 + direction, 1)), date: selectedDate };
  }

  const d = parseLocalDate(selectedDate);
  d.setDate(d.getDate() + direction * (mode === 'week' ? 7 : 1));
  return { month: monthValue(d), date: dateValue(d) };
}

function rangeLabel(mode, selectedDate, selectedMonth) {
  if (mode === 'month') {
    const [year, month] = selectedMonth.split('-').map(Number);
    return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' })
      .format(new Date(year, month - 1, 1));
  }

  const { start, end } = rangeBounds(mode, selectedDate, selectedMonth);
  const startDate = new Date(start);
  if (mode === 'day') {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(startDate);
  }
  const endDate = new Date(new Date(end).getTime() - 1);
  const left = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(startDate);
  const right = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(endDate);
  return `${left} – ${right}`;
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(n);
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

function elapsedLabel(value, now = Date.now()) {
  if (!value) return '—';
  const t = new Date(value).getTime();
  if (!Number.isFinite(t)) return '—';
  const ms = Math.max(0, now - t);
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return '<1m';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

function getDisplayStatus(quote, now = Date.now()) {
  return sharedGetQuoteDisplayStatus(quote, now, STALE_AFTER_MS);
}

function latestTimestamp(q) {
  return new Date(
    q.updated_at || q.bridged_back_at || q.started_at || q.created_at || 0
  ).getTime();
}

function reasonLabel(outcome) {
  return (
    ALL_NOT_CLOSED_REASONS.find(([value]) => value === outcome)?.[1] ||
    (outcome === 'sold'
      ? 'Closed'
      : outcome === 'follow_up'
        ? 'Follow Up'
        : '—')
  );
}

function toDatetimeLocal(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}


function inferQuoteBusinessType(quote) {
  return sharedInferQuoteBusinessType(quote);
}

function contactMethodLabel(quote) {
  const method = cleanStr(quote?.contact_method).toLowerCase();
  if (method === 'in_person') return 'In Person';
  if (method === 'phone') return 'Over the Phone';
  return 'Contact not recorded';
}

function quoteBusinessTypeLabel(quote) {
  return inferQuoteBusinessType(quote) === 'existing_client'
    ? 'Existing Client / Re-Write'
    : 'New Quote';
}

function inferExistingClientReason(quote) {
  return sharedInferExistingClientReason(quote);
}

function existingClientReasonLabel(quote) {
  const reason = inferExistingClientReason(quote);
  return EXISTING_CLIENT_REASONS.find(([value]) => value === reason)?.[1] || 'Existing Client / Re-Write';
}

function hasLicenseSignal(quote) {
  return Boolean(quote?.any_license_entered || Number(quote?.drivers_with_license || 0) > 0);
}

function hasFullVinSignal(quote) {
  return Boolean(quote?.any_full_vin_entered || Number(quote?.vehicles_with_full_vin || 0) > 0);
}

function firstYesConfirmed(quote) {
  return quote?.first_yes_ready_now === true;
}

function secondYesConfirmed(quote) {
  return sharedSecondYesConfirmed(quote);
}

function thirdYesConfirmed(quote) {
  return sharedGetThreeYesStage(quote) === 3;
}

function threeYesStage(quote) {
  return sharedGetThreeYesStage(quote);
}

function threeYesEvidenceLabel(quote) {
  const drivers = Number(quote?.driver_count || 0);
  const licensed = Number(quote?.drivers_with_license || 0);
  const vehicles = Number(quote?.vehicle_count || 0);
  const fullVins = Number(quote?.vehicles_with_full_vin || 0);
  const payment = cleanStr(quote?.payment_method);
  const stage = threeYesStage(quote);

  if (stage === 3) {
    return payment
      ? `Payment ready: ${payment === 'cash' ? 'Cash' : payment === 'card' ? 'Card' : payment}`
      : 'Carrier bridge detected after ID/license + VIN';
  }
  if (stage === 2) return `${licensed}/${drivers || 0} licenses · ${fullVins}/${vehicles || 0} full VINs`;
  if (stage === 1) return 'Customer confirmed they are ready to start if the price works';
  return 'Regular quote';
}

function quoteStage(quote) {
  const stage = threeYesStage(quote);
  if (stage === 3) return { label: '3rd Yes', tone: 'third', stage };
  if (stage === 2) return { label: '2nd Yes', tone: 'second', stage };
  if (stage === 1) return { label: '1st Yes', tone: 'first', stage };
  if (Number(quote?.driver_count || 0) > 0 || Number(quote?.vehicle_count || 0) > 0) {
    return { label: 'Quote', tone: 'active', stage: 0 };
  }
  return { label: 'Early Quote', tone: 'early', stage: 0 };
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function currentQuoteForGroup(group) {
  return group?.quotes?.[0] || {};
}

function latestGroupNote(group) {
  const notes = safeArray(group?.quotes)
    .map((quote) => ({
      text: cleanStr(quote?.latest_internal_note || quote?.agent_notes || quote?.captured_note),
      at: quote?.latest_internal_note_at || quote?.updated_at || quote?.bridged_back_at || quote?.started_at || quote?.created_at || null,
    }))
    .filter((note) => note.text)
    .sort((a, b) => new Date(b.at || 0).getTime() - new Date(a.at || 0).getTime());

  return notes[0] || null;
}

function groupFollowUpAt(group) {
  const followUps = safeArray(group?.quotes)
    .map((quote) => quote?.follow_up_at || null)
    .filter(Boolean)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

  return group?.outcome?.followUpAt || followUps[0] || null;
}

function localDateKey(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function calendarMonthForView(timeView, selectedDate, selectedMonth) {
  return timeView === 'month' ? selectedMonth : monthValue(parseLocalDate(selectedDate));
}

function briefText(value, max = 92) {
  const text = cleanStr(value);
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1).trim()}…` : text;
}

function isGroupClosed(group, now = Date.now()) {
  return sharedIsGroupClosed(group, { now, staleAfterMs: STALE_AFTER_MS });
}

function isGroupLive(group, now = Date.now()) {
  return sharedIsGroupLive(group, { now, staleAfterMs: STALE_AFTER_MS });
}

function isDidNotRewriteGroup(group) {
  if (group?.outcome?.status !== 'not_closed') return false;
  const outcome = cleanStr(group?.outcome?.source?.outcome).toLowerCase();
  return (
    outcome === 'did_not_rw_stayed_current_carrier' ||
    outcome === 'did_not_rewrite_current_carrier'
  );
}


function parseEventIndex(eventType, prefix) {
  if (!String(eventType || '').startsWith(prefix)) return null;
  const value = Number(String(eventType).slice(prefix.length));
  return Number.isFinite(value) ? value : null;
}

function reconcileTimelineEvents(quote, events = []) {
  const validDriverIndexes = new Set(
    safeArray(quote?.drivers_summary)
      .filter((row) => row?.licenseEntered)
      .map((row) => Number(row.index))
      .filter(Number.isFinite)
  );

  const validVehicleIndexes = new Set(
    safeArray(quote?.vehicles_summary)
      .filter((row) => row?.fullVinEntered)
      .map((row) => Number(row.index))
      .filter(Number.isFinite)
  );

  return [...events]
    .filter((event) => {
      const type = String(event?.event_type || '');
      const driverIndex = parseEventIndex(type, 'LICENSE_ENTERED_DRIVER_');
      if (driverIndex !== null) return validDriverIndexes.has(driverIndex);

      const vehicleIndex = parseEventIndex(type, 'FULL_VIN_ENTERED_VEHICLE_');
      if (vehicleIndex !== null) return validVehicleIndexes.has(vehicleIndex);

      if (type === 'FIRST_LICENSE_ENTERED') return Boolean(quote?.any_license_entered);
      if (type === 'FIRST_FULL_VIN_ENTERED') return Boolean(quote?.any_full_vin_entered);
      if (type === 'HIGH_INTENT_DETECTED') return hasLicenseSignal(quote) || hasFullVinSignal(quote);
      return true;
    })
    .sort((a, b) => new Date(a.event_at || 0) - new Date(b.event_at || 0));
}

function eventDisplay(event) {
  const type = String(event?.event_type || '');
  const metadata = event?.metadata || {};

  if (type === 'QUOTE_STARTED') return 'Quote started';
  if (type === 'FIRST_LICENSE_ENTERED') return 'Driver license detected';
  if (type === 'FIRST_FULL_VIN_ENTERED') return 'Full 17-character VIN detected';
  if (type === 'HIGH_INTENT_DETECTED') return 'ID / VIN intent signal detected';
  if (type === 'BRIDGE_BACK') return metadata.carrier ? `Bridge Back · ${metadata.carrier}` : 'Bridge Back completed';
  if (type === 'POLICY_BOUND') return 'Policy bound / closed';

  const driverIndex = parseEventIndex(type, 'LICENSE_ENTERED_DRIVER_');
  if (driverIndex !== null) return `Driver ${driverIndex} license entered`;

  const vehicleIndex = parseEventIndex(type, 'FULL_VIN_ENTERED_VEHICLE_');
  if (vehicleIndex !== null) {
    const vehicle = [metadata.year, metadata.make, metadata.model].filter(Boolean).join(' ');
    return vehicle ? `Vehicle ${vehicleIndex} full VIN · ${vehicle}` : `Vehicle ${vehicleIndex} full VIN entered`;
  }

  return event?.event_label || type.replaceAll('_', ' ');
}

function customerOutcomeFromQuotes(quotes) {
  const sorted = [...quotes].sort((a, b) => latestTimestamp(b) - latestTimestamp(a));
  const completed = sorted.filter((q) => getDisplayStatus(q) === 'completed');

  // A new active attempt must not inherit an older Lost Deal / Walk / Follow-Up
  // disposition from the same customer. The newest quote attempt is authoritative.
  const latest = sorted[0] || null;
  const latestOutcome = cleanStr(latest?.outcome).toLowerCase();
  const latestHasDisposition = Boolean(
    latestOutcome === 'sold' ||
    latestOutcome === 'follow_up' ||
    ALL_NOT_CLOSED_REASONS.some(([value]) => value === latestOutcome) ||
    latest?.follow_up_needed
  );
  // POLICY_BOUND is authoritative for the CURRENT quote attempt.
  // If TurboRater/extension reports POLICY_BOUND, the quote is closed even when
  // the agent never completed the 3 Yes workflow or manually selected Closed.
  if (latest?.policy_bound_detected === true) {
    return {
      status: 'closed',
      source: latest,
      reason: 'Policy Bound (Auto Detected)',
      systemClosed: true,
      policyBoundAt: latest.policy_bound_at || null,
    };
  }

  if (latest && getDisplayStatus(latest) === 'in_progress' && !latestHasDisposition) {
    return {
      status: 'open',
      source: latest,
      reason: sorted.slice(1).some((q) => q.outcome === 'lost_deal')
        ? 'Re-opened Lost Deal'
        : 'Open',
      reopenedLostDeal: sorted.slice(1).some((q) => q.outcome === 'lost_deal'),
    };
  }

  // Workflow disposition is authoritative even when TurboRater never bridged back.
  // This lets admin/supervisor close, not-close, or follow-up an in-progress quote
  // and immediately removes it from Live Activity.
  const sold = sorted.find((q) => q.outcome === 'sold');
  if (sold) return { status: 'closed', source: sold, reason: 'Closed / Policy Sold' };

  const followUpOverride = sorted.find((q) => q.outcome === 'follow_up' || q.follow_up_needed);
  if (followUpOverride) {
    return {
      status: 'follow_up',
      source: followUpOverride,
      reason: 'Follow Up',
      followUpAt: followUpOverride.follow_up_at || null,
    };
  }

  const notClosedOverride = sorted.find((q) =>
    ALL_NOT_CLOSED_REASONS.map(([value]) => value).includes(q.outcome)
  );
  if (notClosedOverride) {
    return {
      status: 'not_closed',
      source: notClosedOverride,
      reason: reasonLabel(notClosedOverride.outcome),
    };
  }

  const systemBound = completed.find(
    (q) =>
      q.policy_bound_detected === true ||
      q.system_outcome_signal === 'sold' ||
      String(q.bridge_policy_status_value || '').toLowerCase() === 'bound' ||
      String(q.bridge_policy_status || '').toLowerCase() === 'policy bound'
  );

  if (systemBound) {
    return {
      status: 'closed',
      source: systemBound,
      reason: 'Policy Bound (TurboRater)',
      systemClosed: true,
    };
  }


  return {
    status: 'open',
    source: completed[0] || sorted[0] || null,
    reason: 'Open',
  };
}

function groupQuotesByCustomer(quotes, now, officeRegions = {}) {
  const map = new Map();

  for (const quote of quotes) {
    const key = quote.matrix_customer_id
      ? `customer:${quote.matrix_customer_id}`
      : `quote:${quote.id}`;

    if (!map.has(key)) {
      map.set(key, {
        key,
        matrixCustomerId: quote.matrix_customer_id || null,
        quotes: [],
      });
    }

    map.get(key).quotes.push(quote);
  }

  return [...map.values()]
    .map((group) => {
      const sorted = [...group.quotes].sort((a, b) => latestTimestamp(b) - latestTimestamp(a));
      const latest = sorted[0];
      const completed = sorted.filter((q) => getDisplayStatus(q, now) === 'completed');
      const inProgress = sorted.filter((q) => getDisplayStatus(q, now) === 'in_progress');
      const stale = sorted.filter((q) => getDisplayStatus(q, now) === 'stale');
      const premiums = completed.map((q) => Number(q.total_premium)).filter(Number.isFinite);
      const outcome = customerOutcomeFromQuotes(sorted);
      const office = normalizeOffice(latest?.office || 'Unknown');

      return {
        ...group,
        quotes: sorted,
        latest,
        completedCount: completed.length,
        inProgressCount: inProgress.length,
        staleCount: stale.length,
        bestPremium: premiums.length ? Math.min(...premiums) : null,
        customerName: latest?.customer_name || 'Unnamed Customer',
        phone: latest?.phone || '',
        email: latest?.email || '',
        agentName: latest?.captured_agent_name || latest?.agent_email || 'Unknown agent',
        office,
        region: cleanStr(officeRegions[office]) || 'Unassigned',
        lastActivityAt:
          latest?.updated_at ||
          latest?.bridged_back_at ||
          latest?.started_at ||
          latest?.created_at,
        outcome,
        reopenedLostDeal: Boolean(outcome?.reopenedLostDeal),
        yesStage: Math.max(0, ...sorted.map(threeYesStage)),
        stageQuote: sorted.find((q) => threeYesStage(q) === Math.max(0, ...sorted.map(threeYesStage))) || latest,
        businessType: inferQuoteBusinessType(latest),
        existingClientReason: inferQuoteBusinessType(latest) === 'existing_client'
          ? inferExistingClientReason(latest)
          : '',
      };
    })
    .sort((a, b) => new Date(b.lastActivityAt || 0) - new Date(a.lastActivityAt || 0));
}



export default function SupervisorQuoteOperations() {
  const { supabaseClient, profile } = useAuth();
  const userRole = cleanStr(profile?.role || profile?.user_role).toLowerCase();
  const profileOffice = normalizeOffice(profile?.office);
  const profileRegion = cleanStr(profile?.region);


  const [activeTab, setActiveTab] = useState('live');
  const [timeView, setTimeView] = useState('month');
  const [month, setMonth] = useState(monthValue());
  const [selectedDate, setSelectedDate] = useState(dateValue());
  const [quotes, setQuotes] = useState([]);
  const [quoteEvents, setQuoteEvents] = useState([]);
  const [quoteInternalNotes, setQuoteInternalNotes] = useState([]);
  const [officeRegions, setOfficeRegions] = useState({});
  const [loading, setLoading] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [agentFilter, setAgentFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [now, setNow] = useState(Date.now());
  const refreshTimer = useRef(null);
  const fetchRequestRef = useRef(0);
  const officeAccessRefreshTimer = useRef(null);
  const [officeDevices, setOfficeDevices] = useState([]);
  const [officeDevicesLoading, setOfficeDevicesLoading] = useState(false);
  const [officeDeviceMessage, setOfficeDeviceMessage] = useState('');


  const [agentEntry, setAgentEntry] = useState({
    status: '',
    reason: '',
    follow_up_at: '',
    notes: '',
  });

  const fetchOfficeSettings = useCallback(async () => {
    if (!supabaseClient) return;
    setSettingsLoading(true);

    const { data, error: settingsError } = await supabaseClient
      .from('office_dashboard_settings')
      .select('office_code, region');

    if (settingsError) {
      console.error('[SupervisorQuoteOperations] office settings query failed:', settingsError);
      setError((current) => current || `Could not load office regions: ${settingsError.message}`);
      setSettingsLoading(false);
      return;
    }

    const regionMap = {};
    (data || []).forEach((row) => {
      const officeCode = normalizeOffice(row.office_code);
      if (!officeCode || officeCode === 'Unknown') return;
      regionMap[officeCode] = cleanStr(row.region) || 'Unassigned';
    });

    setOfficeRegions(regionMap);
    setSettingsLoading(false);
  }, [supabaseClient]);

  const fetchOfficeDevices = useCallback(async ({ quiet = false } = {}) => {
    if (!supabaseClient || userRole !== 'supervisor') return;

    if (!quiet) {
      setOfficeDevicesLoading(true);
      setOfficeDeviceMessage('');
    }

    const { data, error: deviceError } = await supabaseClient
      .rpc('get_supervisor_office_devices');

    if (deviceError) {
      console.error('[SupervisorQuoteOperations] office device query failed:', deviceError);
      if (!quiet) {
        setOfficeDeviceMessage(`Could not load office devices: ${deviceError.message}`);
        setOfficeDevicesLoading(false);
      }
      return;
    }

    setOfficeDevices(data || []);
    if (!quiet) setOfficeDevicesLoading(false);
  }, [supabaseClient, userRole]);

  const fetchQuotes = useCallback(
    async ({ quiet = false } = {}) => {
      if (!supabaseClient) return;
      const requestId = ++fetchRequestRef.current;
      if (!quiet) setLoading(true);
      setError('');

      if (!profileOffice || profileOffice === 'Unknown') {
        setError('Your supervisor profile does not have an office assigned.');
        if (!quiet) setLoading(false);
        return;
      }

      const { start, end } = rangeBounds(timeView, selectedDate, month);
      // Supabase/PostgREST can cap a single response. Page every primary dataset
      // so the supervisor always classifies the complete office history.
      const fetchAllPaged = async (buildQuery, label, pageSize = 1000) => {
        const rows = [];
        let from = 0;

        while (true) {
          const { data, error } = await buildQuery()
            .range(from, from + pageSize - 1);

          if (error) {
            console.error(`[SupervisorQuoteOperations] ${label} page failed at ${from}:`, error);
            return { data: rows, error };
          }

          const page = data || [];
          rows.push(...page);
          if (page.length < pageSize) break;
          from += pageSize;
        }

        return { data: rows, error: null };
      };

      const [historyResult, liveResult, followUpWorkflowResult] = await Promise.all([
        fetchAllPaged(
          () => supabaseClient
            .from('quote_log_view')
            .select('*')
            .eq('office', profileOffice)
            .gte('started_at', start)
            .lt('started_at', end)
            .order('started_at', { ascending: false })
            .order('id', { ascending: false }),
          'quote history'
        ),
        // Live Activity is intentionally independent of Day / Week / Month.
        fetchAllPaged(
          () => supabaseClient
            .from('quote_log_view')
            .select('*')
            .eq('office', profileOffice)
            .eq('status', 'in_progress')
            .order('started_at', { ascending: false })
            .order('id', { ascending: false }),
          'live quotes'
        ),
        // Follow-Ups is an office work queue, not a history-date bucket.
        // Load every scheduled follow-up the supervisor can see. Calendar applies
        // its own month filter later; the Follow-Ups tab must not lose future
        // appointments just because Day / Week / Month is currently set to August.
        fetchAllPaged(
          () => supabaseClient
            .from('quote_workflow')
            .select('quote_id, follow_up_at')
            .or('follow_up_needed.eq.true,outcome.eq.follow_up')
            .not('follow_up_at', 'is', null)
            .order('follow_up_at', { ascending: true })
            .order('quote_id', { ascending: true }),
          'follow-up workflow'
        ),
      ]);

      if (historyResult.error || liveResult.error || followUpWorkflowResult.error) {
        const queryError = historyResult.error || liveResult.error || followUpWorkflowResult.error;
        console.error('[SupervisorQuoteOperations] query failed:', queryError);
        setError(`Could not load Quote Operations: ${queryError.message}`);
        if (!quiet) setLoading(false);
        return;
      }

      const followUpQuoteIds = [...new Set((followUpWorkflowResult.data || []).map((row) => row.quote_id).filter(Boolean))];
      const followUpQuoteRows = [];
      for (let index = 0; index < followUpQuoteIds.length; index += 200) {
        const ids = followUpQuoteIds.slice(index, index + 200);
        const { data, error: followUpQuoteError } = await supabaseClient
          .from('quote_log_view')
          .select('*')
          .eq('office', profileOffice)
          .in('id', ids);
        if (followUpQuoteError) {
          console.error('[SupervisorQuoteOperations] follow-up quote query failed:', followUpQuoteError);
        } else {
          followUpQuoteRows.push(...(data || []));
        }
      }

      const rowMap = new Map();
      [...(historyResult.data || []), ...(liveResult.data || []), ...followUpQuoteRows].forEach((row) => {
        if (row?.id) rowMap.set(row.id, row);
      });
      const baseRows = [...rowMap.values()];
      const quoteIds = [...new Set(baseRows.map((row) => row.id).filter(Boolean))];
      const supplementalRows = [];
      const workflowRows = [];
      const eventRows = [];
      const internalNoteRows = [];
      const dealSaveRows = [];

      for (let index = 0; index < quoteIds.length; index += 200) {
        const ids = quoteIds.slice(index, index + 200);

        const fetchQuoteEventsForIds = async (quoteIdBatch) => {
          const rows = [];
          const pageSize = 1000;
          let from = 0;

          while (true) {
            const { data, error } = await supabaseClient
              .from('quote_events')
              .select('id, quote_id, capture_id, event_type, event_label, event_at, metadata, created_at')
              .in('quote_id', quoteIdBatch)
              .order('event_at', { ascending: true })
              .range(from, from + pageSize - 1);

            if (error) return { data: rows, error };
            const page = data || [];
            rows.push(...page);
            if (page.length < pageSize) break;
            from += pageSize;
          }

          return { data: rows, error: null };
        };

        const [
          { data: quoteData, error: quoteError },
          { data: eventsData, error: eventsError },
          { data: workflowData, error: workflowError },
          { data: notesData, error: notesError },
          { data: dealSaveData, error: dealSaveError },
        ] = await Promise.all([
          supabaseClient
            .from('quotes')
            .select('id, status, extension_version, carrier, total_premium, down_payment, monthly_payment, bridged_back_at, bridge_policy_status, bridge_policy_status_value, system_outcome_signal, carrier_bridge_started_at, carrier_bridge_carrier, matrix_bridge_back_at, driver_count, drivers_with_license, any_license_entered, first_license_entered_at, vehicle_count, vehicles_with_full_vin, any_full_vin_entered, first_full_vin_entered_at, high_intent_detected, high_intent_detected_at, completeness_observed_at, last_live_activity_at, drivers_summary, vehicles_summary, turborater_office, office_source, office_mismatch, captured_device_id')
            .in('id', ids),
          fetchQuoteEventsForIds(ids),
          supabaseClient
            .from('quote_workflow')
            .select('quote_id, contact_method, quote_business_type, existing_client_reason, first_yes_ready_now, second_yes_id_vin, third_yes_payment_ready, payment_method, first_yes_recorded_at, second_yes_recorded_at, third_yes_recorded_at, outcome, follow_up_needed, follow_up_at, agent_notes, not_closed_explanation, lost_deal_manager_name, lost_deal_broker_fee')
            .in('quote_id', ids),
          supabaseClient
            .from('quote_internal_notes')
            .select('id, quote_id, note_text, author_email, author_name, author_role, note_source, created_at')
            .in('quote_id', ids)
            .order('created_at', { ascending: false }),
          supabaseClient
            .from('quote_deal_save_requests')
            .select('id, quote_id, customer_name, office, requested_by_email, requested_by_name, requested_by_role, current_broker_fee, premium, status, created_at, updated_at')
            .in('quote_id', ids)
            .order('created_at', { ascending: false }),
        ]);

        if (quoteError) {
          console.error('[SupervisorQuoteOperations] completeness query failed:', quoteError);
        } else {
          supplementalRows.push(...(quoteData || []));
        }

        if (eventsError) {
          console.error('[SupervisorQuoteOperations] quote events query failed:', eventsError);
        } else {
          eventRows.push(...(eventsData || []));
        }

        if (workflowError) {
          console.error('[SupervisorQuoteOperations] 3 Yes workflow query failed:', workflowError);
        } else {
          workflowRows.push(...(workflowData || []));
        }

        if (notesError) {
          console.error('[SupervisorQuoteOperations] internal notes query failed:', notesError);
        } else {
          internalNoteRows.push(...(notesData || []));
        }

        if (dealSaveError) {
          console.error('[SupervisorQuoteOperations] Deal Save request query failed:', dealSaveError);
        } else {
          dealSaveRows.push(...(dealSaveData || []));
        }
      }

      const supplementalById = new Map(supplementalRows.map((row) => [row.id, row]));
      const workflowById = new Map(workflowRows.map((row) => [row.quote_id, row]));

      // quote_events is the most reliable source for an auto-detected policy bind.
      // Attach that signal directly to the quote row so classification can remove
      // the quote from Live Activity immediately, even if quote.status still says
      // in_progress and the agent never completed the workflow form.
      const policyBoundByQuoteId = new Map();
      eventRows.forEach((event) => {
        if (String(event?.event_type || '').toUpperCase() !== 'POLICY_BOUND') return;
        const existing = policyBoundByQuoteId.get(event.quote_id);
        const eventAt = event.event_at || event.created_at || null;
        if (!existing || new Date(eventAt || 0).getTime() > new Date(existing || 0).getTime()) {
          policyBoundByQuoteId.set(event.quote_id, eventAt);
        }
      });

      const latestInternalNoteByQuoteId = new Map();
      internalNoteRows.forEach((note) => {
        if (!note?.quote_id) return;
        const existing = latestInternalNoteByQuoteId.get(note.quote_id);
        const noteAt = note.created_at || null;
        if (!existing || new Date(noteAt || 0).getTime() > new Date(existing.created_at || 0).getTime()) {
          latestInternalNoteByQuoteId.set(note.quote_id, note);
        }
      });

      const latestDealSaveByQuoteId = new Map();
      dealSaveRows.forEach((request) => {
        if (!request?.quote_id) return;
        const existing = latestDealSaveByQuoteId.get(request.quote_id);
        const requestAt = new Date(request.updated_at || request.created_at || 0).getTime();
        const existingAt = new Date(existing?.updated_at || existing?.created_at || 0).getTime();
        if (!existing || requestAt > existingAt) latestDealSaveByQuoteId.set(request.quote_id, request);
      });

      const mergedRows = baseRows.map((row) => {
        const policyBoundAt = policyBoundByQuoteId.get(row.id) || null;
        const latestInternalNote = latestInternalNoteByQuoteId.get(row.id) || null;
        const latestDealSaveRequest = latestDealSaveByQuoteId.get(row.id) || null;
        return {
          ...row,
          ...(supplementalById.get(row.id) || {}),
          ...(workflowById.get(row.id) || {}),
          policy_bound_detected: Boolean(policyBoundAt),
          policy_bound_at: policyBoundAt,
          latest_internal_note: latestInternalNote?.note_text || null,
          latest_internal_note_at: latestInternalNote?.created_at || null,
          latest_deal_save_request: latestDealSaveRequest,
        };
      });

      // Ignore stale overlapping refresh responses. This prevents the live UI
      // from briefly jumping backward when several realtime events arrive together.
      if (requestId !== fetchRequestRef.current) return;

      setQuotes(mergedRows);
      setQuoteEvents(eventRows);
      setQuoteInternalNotes(internalNoteRows);
      if (!quiet) setLoading(false);
    },
    [timeView, selectedDate, month, supabaseClient, profileOffice]
  );

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchQuotes(), fetchOfficeSettings(), fetchOfficeDevices()]);
  }, [fetchQuotes, fetchOfficeSettings, fetchOfficeDevices]);

  useEffect(() => {
    fetchOfficeSettings();
  }, [fetchOfficeSettings]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  useEffect(() => {
    fetchOfficeDevices();
  }, [fetchOfficeDevices]);

  useEffect(() => {
    // Realtime is primary; this quiet fallback catches missed device events.
    const id = setInterval(() => fetchOfficeDevices({ quiet: true }), 15000);
    return () => clearInterval(id);
  }, [fetchOfficeDevices]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    // Realtime is primary. This quiet fallback refresh catches missed websocket
    // events without flashing loading states or resetting the selected tab/drawer.
    const id = setInterval(() => fetchQuotes({ quiet: true }), 30000);
    return () => clearInterval(id);
  }, [fetchQuotes]);

  useEffect(() => {
    if (!supabaseClient) return undefined;

    const scheduleRefresh = () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => fetchQuotes({ quiet: true }), 850);
    };

    const channel = supabaseClient
      .channel(`supervisor-quote-operations-${profileOffice}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quotes' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quote_workflow' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quote_events' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quote_internal_notes' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quote_deal_save_requests' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'extension_devices' }, () => {
        if (officeAccessRefreshTimer.current) clearTimeout(officeAccessRefreshTimer.current);
        officeAccessRefreshTimer.current = setTimeout(
          () => fetchOfficeDevices({ quiet: true }),
          500
        );
      })
      .subscribe();

    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      if (officeAccessRefreshTimer.current) clearTimeout(officeAccessRefreshTimer.current);
      supabaseClient.removeChannel(channel);
    };
  }, [fetchQuotes, fetchOfficeDevices, supabaseClient, profileOffice]);

  const customerGroups = useMemo(
    () => groupQuotesByCustomer(quotes, now, officeRegions),
    [quotes, now, officeRegions]
  );


  useEffect(() => {
    if (!selectedCustomer?.key) return;
    const refreshed = customerGroups.find((group) => group.key === selectedCustomer.key);
    if (refreshed) setSelectedCustomer(refreshed);
  }, [customerGroups, selectedCustomer?.key]);

  const agents = useMemo(() => {
    const values = new Set();
    customerGroups.forEach((group) => {
      if (group.office !== profileOffice) return;
      if (group.agentName) values.add(group.agentName);
    });
    return [...values].sort((a, b) => a.localeCompare(b));
  }, [customerGroups, profileOffice]);

  const scopeGroups = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return customerGroups.filter((group) => {
      if (group.office !== profileOffice) return false;
      if (agentFilter !== 'all' && group.agentName !== agentFilter) return false;

      if (!needle) return true;

      const haystack = [
        group.customerName,
        group.matrixCustomerId,
        group.phone,
        group.email,
        group.agentName,
        group.office,
        group.region,
        ...group.quotes.flatMap((q) => [
          q.carrier,
          q.matrix_quote_id,
          q.rater_id,
          q.captured_note,
          q.agent_notes,
          q.lead_source,
          q.marketing_number,
        ]),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(needle);
    });
  }, [customerGroups, profileOffice, agentFilter, search]);

  const selectedBounds = useMemo(
    () => rangeBounds(timeView, selectedDate, month),
    [timeView, selectedDate, month]
  );

  const historicalGroups = useMemo(() => {
    const startMs = new Date(selectedBounds.start).getTime();
    const endMs = new Date(selectedBounds.end).getTime();
    return scopeGroups.filter((group) =>
      group.quotes.some((quote) => {
        const t = new Date(quote.started_at || quote.created_at || 0).getTime();
        return Number.isFinite(t) && t >= startMs && t < endMs;
      })
    );
  }, [scopeGroups, selectedBounds]);

  const liveGroups = useMemo(
    () => scopeGroups.filter((group) => isGroupLive(group, now)),
    [scopeGroups, now]
  );

  const myQuoteNames = useMemo(
    () => new Set(
      [
        profile?.csr_name,
        profile?.turborater_agent_name,
        profile?.full_name,
      ]
        .map(normalizePersonName)
        .filter(Boolean)
    ),
    [profile?.csr_name, profile?.turborater_agent_name, profile?.full_name]
  );

  const myLiveGroups = useMemo(
    () => liveGroups.filter((group) => {
      if (group.office !== profileOffice) return false;

      const latest = group.quotes?.[0] || group.latest || {};
      const capturedName = normalizePersonName(
        latest.captured_agent_name ||
        latest.csr_name ||
        group.agentName
      );
      const quoteEmail = cleanStr(latest.agent_email).toLowerCase();
      const profileEmail = cleanStr(profile?.email).toLowerCase();

      return (
        (profileEmail && quoteEmail && profileEmail === quoteEmail) ||
        (capturedName && myQuoteNames.has(capturedName))
      );
    }),
    [liveGroups, profileOffice, profile?.email, myQuoteNames]
  );

  const myActiveGroup = myLiveGroups[0] || null;


  const quoteEventsByQuote = useMemo(() => {
    const map = {};
    quoteEvents.forEach((event) => {
      if (!event?.quote_id) return;
      if (!map[event.quote_id]) map[event.quote_id] = [];
      map[event.quote_id].push(event);
    });
    return map;
  }, [quoteEvents]);

  const internalNotesByQuote = useMemo(() => {
    const map = {};
    quoteInternalNotes.forEach((note) => {
      if (!note?.quote_id) return;
      if (!map[note.quote_id]) map[note.quote_id] = [];
      map[note.quote_id].push(note);
    });
    Object.values(map).forEach((rows) =>
      rows.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    );
    return map;
  }, [quoteInternalNotes]);

  const closedGroups = useMemo(
    () => historicalGroups.filter((group) => isGroupClosed(group, now)),
    [historicalGroups, now]
  );


  const calendarMonth = useMemo(
    () => calendarMonthForView(timeView, selectedDate, month),
    [timeView, selectedDate, month]
  );

  const calendarFollowUpGroups = useMemo(() => {
    const { start, end } = monthBounds(calendarMonth);
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();
    return scopeGroups
      .filter((group) => sharedIsGroupFollowUp(group, { now, staleAfterMs: STALE_AFTER_MS }))
      .filter((group) => {
        const at = new Date(groupFollowUpAt(group) || 0).getTime();
        return Number.isFinite(at) && at >= startMs && at < endMs;
      })
      .sort((a, b) => new Date(groupFollowUpAt(a) || 0) - new Date(groupFollowUpAt(b) || 0));
  }, [scopeGroups, calendarMonth, now]);

  // Follow-Ups is the complete visible-office queue. Do not filter it by the
  // quote history Day / Week / Month selection. The scheduled follow_up_at date
  // controls ordering; Calendar separately limits appointments to its month.
  const followUpGroups = useMemo(
    () => scopeGroups
      .filter((group) => sharedIsGroupFollowUp(group, { now, staleAfterMs: STALE_AFTER_MS }))
      .filter((group) => Boolean(groupFollowUpAt(group)))
      .sort((a, b) => new Date(groupFollowUpAt(a) || 0) - new Date(groupFollowUpAt(b) || 0)),
    [scopeGroups, now]
  );

  const quoteGroups = useMemo(
    () => historicalGroups.filter((group) => {
      if (isGroupLive(group, now)) return false;
      return sharedIsGroupRegularQuote(group, {
        now,
        staleAfterMs: STALE_AFTER_MS,
      });
    }),
    [historicalGroups, now]
  );

  const newQuoteGroups = useMemo(
    () => quoteGroups.filter((group) => group.businessType !== 'existing_client'),
    [quoteGroups]
  );

  const existingClientQuoteGroups = useMemo(
    () => quoteGroups.filter((group) => group.businessType === 'existing_client'),
    [quoteGroups]
  );

  const paymentQuoteGroups = useMemo(
    () => existingClientQuoteGroups.filter((group) => group.existingClientReason === 'rewrite_payment'),
    [existingClientQuoteGroups]
  );

  const endorsementQuoteGroups = useMemo(
    () => existingClientQuoteGroups.filter((group) => group.existingClientReason === 'rewrite_endorsement'),
    [existingClientQuoteGroups]
  );

  const renewalQuoteGroups = useMemo(
    () => existingClientQuoteGroups.filter((group) => group.existingClientReason === 'renewal'),
    [existingClientQuoteGroups]
  );

  const reinstatementQuoteGroups = useMemo(
    () => existingClientQuoteGroups.filter((group) => group.existingClientReason === 'reinstatement'),
    [existingClientQuoteGroups]
  );

  const recentRewriteQuoteGroups = useMemo(
    () => existingClientQuoteGroups.filter((group) => group.existingClientReason === 'rewrite_recent_policy'),
    [existingClientQuoteGroups]
  );

  const unclassifiedExistingQuoteGroups = useMemo(
    () => existingClientQuoteGroups.filter((group) => !group.existingClientReason),
    [existingClientQuoteGroups]
  );

  const newClosedGroups = useMemo(
    () => closedGroups.filter((group) => group.businessType !== 'existing_client'),
    [closedGroups]
  );

  const existingClientClosedGroups = useMemo(
    () => closedGroups.filter((group) => group.businessType === 'existing_client'),
    [closedGroups]
  );

  const paymentClosedGroups = useMemo(
    () => existingClientClosedGroups.filter((group) => group.existingClientReason === 'rewrite_payment'),
    [existingClientClosedGroups]
  );

  const endorsementClosedGroups = useMemo(
    () => existingClientClosedGroups.filter((group) => group.existingClientReason === 'rewrite_endorsement'),
    [existingClientClosedGroups]
  );

  const renewalClosedGroups = useMemo(
    () => existingClientClosedGroups.filter((group) => group.existingClientReason === 'renewal'),
    [existingClientClosedGroups]
  );

  const reinstatementClosedGroups = useMemo(
    () => existingClientClosedGroups.filter((group) => group.existingClientReason === 'reinstatement'),
    [existingClientClosedGroups]
  );

  const recentRewriteClosedGroups = useMemo(
    () => existingClientClosedGroups.filter((group) => group.existingClientReason === 'rewrite_recent_policy'),
    [existingClientClosedGroups]
  );

  const unclassifiedExistingClosedGroups = useMemo(
    () => existingClientClosedGroups.filter((group) => !group.existingClientReason),
    [existingClientClosedGroups]
  );

  const attentionGroups = useMemo(() => {
    return historicalGroups
      .map((group) => {
        const item = sharedGetGroupAttentionItem(group, {
          now,
          staleAfterMs: STALE_AFTER_MS,
        });
        return item || null;
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.attention.sortValue !== b.attention.sortValue) {
          return a.attention.sortValue - b.attention.sortValue;
        }
        return new Date(a.group.lastActivityAt || 0) - new Date(b.group.lastActivityAt || 0);
      });
  }, [historicalGroups, now]);

  const lostDealAttention = useMemo(
    () => attentionGroups.filter((item) => item.attention?.category === ATTENTION_CATEGORIES.LOST_DEAL),
    [attentionGroups]
  );

  const walkAttention = useMemo(
    () => attentionGroups.filter((item) => item.attention?.category === ATTENTION_CATEGORIES.WALK),
    [attentionGroups]
  );

  const carrierBridgeAttention = useMemo(
    () => attentionGroups.filter((item) => [
      ATTENTION_CATEGORIES.CARRIER_NO_RETURN,
      ATTENTION_CATEGORIES.CARRIER_RETURN_NO_OUTCOME,
      ATTENTION_CATEGORIES.CARRIER_NO_OUTCOME,
    ].includes(item.attention?.category)),
    [attentionGroups]
  );

  const unresolvedYesAttention = useMemo(
    () => attentionGroups.filter((item) => item.attention?.category === ATTENTION_CATEGORIES.STALE_YES),
    [attentionGroups]
  );

  const stats = useMemo(() => {
    const activeAgents = new Set(liveGroups.map((g) => g.agentName).filter(Boolean)).size;
    const activeOffices = new Set(liveGroups.map((g) => g.office).filter(Boolean)).size;

    return {
      customers: historicalGroups.length,
      closed: closedGroups.length,
      live: liveGroups.length,
      attention: attentionGroups.length,
      quotes: quoteGroups.length,
      followups: followUpGroups.length,
      calendarFollowups: calendarFollowUpGroups.length,
      activeAgents,
      activeOffices,
    };
  }, [historicalGroups, closedGroups, liveGroups, attentionGroups, quoteGroups, followUpGroups, calendarFollowUpGroups]);

  const liveByRegion = useMemo(() => {
    const grouped = new Map();

    liveGroups.forEach((group) => {
      const region = group.region || 'Unassigned';
      if (!grouped.has(region)) grouped.set(region, new Map());
      const officeMap = grouped.get(region);
      if (!officeMap.has(group.office)) officeMap.set(group.office, []);
      officeMap.get(group.office).push(group);
    });

    return [...grouped.entries()]
      .map(([region, officeMap]) => ({
        region,
        offices: [...officeMap.entries()]
          .map(([office, groups]) => ({
            office,
            groups: [...groups].sort((a, b) => {
              const stageDiff = Number(b.yesStage || 0) - Number(a.yesStage || 0);
              if (stageDiff) return stageDiff;
              return new Date(b.lastActivityAt || 0).getTime() - new Date(a.lastActivityAt || 0).getTime();
            }),
          }))
          .sort((a, b) => getOfficeNumber(a.office) - getOfficeNumber(b.office)),
      }))
      .sort((a, b) => {
        if (a.region === 'Unassigned') return 1;
        if (b.region === 'Unassigned') return -1;
        return a.region.localeCompare(b.region, undefined, { numeric: true, sensitivity: 'base' });
      });
  }, [liveGroups]);


  const scopeLabel = useMemo(() => {
    const region = profileRegion || officeRegions[profileOffice] || 'Region not assigned';
    return `${profileOffice} · ${region}`;
  }, [profileOffice, profileRegion, officeRegions]);

  const officeLogAccessCount = useMemo(
    () => officeDevices.filter((row) =>
      row.office_log_access === true &&
      row.approval_status === 'approved' &&
      row.is_active === true
    ).length,
    [officeDevices]
  );

  const pendingAdminDeviceCount = useMemo(
    () => officeDevices.filter((row) =>
      row.approval_status !== 'approved' || row.is_active !== true
    ).length,
    [officeDevices]
  );

  const setOfficeLogAccess = async (row, allowAccess) => {
    if (!row?.id) return;
    setOfficeDeviceMessage('');

    const { error: accessError } = await supabaseClient.rpc(
      'set_supervisor_office_log_access',
      {
        p_device_id: row.id,
        p_allow_access: Boolean(allowAccess),
      }
    );

    if (accessError) {
      console.error('[SupervisorQuoteOperations] office log access update failed:', accessError);
      setOfficeDeviceMessage(`Could not update office log access: ${accessError.message}`);
      return;
    }

    await fetchOfficeDevices({ quiet: true });
    setOfficeDeviceMessage(
      allowAccess
        ? `${row.device_name || 'Device'} can now view the ${profileOffice} office log.`
        : `${row.device_name || 'Device'} office log access removed.`
    );
  };

  const openCustomer = (group) => {
    const isSame = selectedCustomer?.key === group.key;

    if (isSame) {
      setSelectedCustomer(null);
      setSaveMessage('');
      return;
    }

    setSelectedCustomer(group);
    setSaveMessage('');

    const current = group.outcome;
    const currentQuote = group.quotes[0] || {};
    setAgentEntry({
      status: current.status === 'open' ? '' : current.status,
      reason: current.status === 'not_closed' ? current.source?.outcome || '' : '',
      follow_up_at: current.status === 'follow_up' ? toDatetimeLocal(current.followUpAt) : '',
      notes: '',
      business_type: currentQuote.quote_business_type || inferQuoteBusinessType(currentQuote),
      existing_client_reason: currentQuote.existing_client_reason || '',
      first_yes: currentQuote.first_yes_ready_now === true ? 'yes' : currentQuote.first_yes_ready_now === false ? 'no' : '',
      second_yes: currentQuote.second_yes_id_vin === true ? 'yes' : currentQuote.second_yes_id_vin === false ? 'no' : 'auto',
      payment_method: currentQuote.payment_method || '',
    });
  };

  const buildOverrideHistoryText = ({
    outcome,
    followUpAt,
    firstYesValue,
    secondYesValue,
    thirdYesValue,
  }) => {
    const businessType =
      agentEntry.business_type === 'existing_client'
        ? 'Existing Client / Re-Write'
        : 'New Quote';

    const rewriteReason =
      agentEntry.business_type === 'existing_client'
        ? existingClientReasonLabel(agentEntry.existing_client_reason) || 'Needs Classification'
        : null;

    const firstYesLabel =
      firstYesValue === true ? 'Yes' : firstYesValue === false ? 'No' : 'Not recorded';

    const secondYesLabel =
      agentEntry.second_yes === 'auto'
        ? 'System detection'
        : secondYesValue === true
          ? 'Yes'
          : secondYesValue === false
            ? 'No'
            : 'Not recorded';

    const thirdYesLabel =
      agentEntry.payment_method === 'cash'
        ? 'Cash'
        : agentEntry.payment_method === 'card'
          ? 'Card'
          : agentEntry.payment_method === 'neither'
            ? 'Neither / Not Ready'
            : thirdYesValue === true
              ? 'Yes'
              : 'Not recorded';

    const outcomeLabel =
      outcome === 'sold'
        ? 'Closed'
        : outcome === 'follow_up'
          ? 'Follow Up'
          : reasonLabel(outcome) || 'Not Closed';

    const parts = [
      'Supervisor override saved',
      `Quote Type: ${businessType}${rewriteReason ? ` - ${rewriteReason}` : ''}`,
      `1st Yes: ${firstYesLabel}`,
      `2nd Yes: ${secondYesLabel}`,
      `3rd Yes / Payment: ${thirdYesLabel}`,
      `Outcome: ${outcomeLabel}`,
    ];

    if (followUpAt) {
      parts.push(`Follow Up: ${formatDateTime(followUpAt)}`);
    }

    return parts.join(' · ');
  };

  const saveCustomerOutcome = async (event) => {
    event.preventDefault();
    if (!selectedCustomer) return;

    // Supervisor overrides must apply to the CURRENT quote attempt.
    // Using an older completed attempt can save a follow-up successfully in
    // quote_workflow while customerOutcomeFromQuotes still treats the newer
    // in-progress attempt as Open, which makes the follow-up disappear from
    // Follow-Ups / Calendar / the refreshed Needs Attention view.
    const targetQuote = selectedCustomer.quotes[0];

    if (!targetQuote?.id) {
      setSaveMessage('Save failed: no quote record is available for this customer.');
      return;
    }

    let outcome = null;
    let followUpNeeded = false;
    let followUpAt = null;

    if (agentEntry.status === 'closed') outcome = 'sold';
    if (agentEntry.status === 'not_closed') outcome = agentEntry.reason || null;
    if (agentEntry.status === 'follow_up') {
      outcome = 'follow_up';
      followUpNeeded = true;
      followUpAt = agentEntry.follow_up_at
        ? new Date(agentEntry.follow_up_at).toISOString()
        : null;
    }

    if (!outcome) {
      setSaveMessage('Choose an outcome before saving.');
      return;
    }

    if (agentEntry.status === 'not_closed' && !agentEntry.reason) {
      setSaveMessage('Choose why the customer was not closed.');
      return;
    }

    if (agentEntry.status === 'not_closed' && agentEntry.reason === 'did_not_rewrite_current_carrier' && !agentEntry.notes.trim()) {
      setSaveMessage('Explain why the customer stayed with the current carrier before saving.');
      return;
    }

    setSaving(true);
    setSaveMessage('');

    const savedAt = new Date().toISOString();
    const firstYesValue = agentEntry.first_yes === '' ? null : agentEntry.first_yes === 'yes';
    const secondYesValue = agentEntry.second_yes === 'auto' ? null : agentEntry.second_yes === 'yes';
    const thirdYesValue = agentEntry.payment_method === ''
      ? null
      : agentEntry.payment_method === 'cash' || agentEntry.payment_method === 'card';

    const payload = {
      quote_id: targetQuote.id,
      quote_reason: targetQuote.quote_reason || null,
      outcome,
      agent_notes: agentEntry.notes.trim() || null,
      follow_up_needed: followUpNeeded,
      follow_up_at: followUpAt,
      completed_by_email: profile?.email || null,
      quote_business_type: agentEntry.business_type || inferQuoteBusinessType(targetQuote),
      existing_client_reason: agentEntry.business_type === 'existing_client'
        ? agentEntry.existing_client_reason || null
        : null,
      not_closed_type: agentEntry.status === 'not_closed' ? agentEntry.reason || null : null,
      not_closed_explanation: agentEntry.status === 'not_closed' && agentEntry.reason === 'did_not_rewrite_current_carrier'
        ? agentEntry.notes.trim()
        : null,
      first_yes_ready_now: firstYesValue,
      second_yes_id_vin: secondYesValue,
      third_yes_payment_ready: thirdYesValue,
      payment_method: agentEntry.payment_method || null,
      first_yes_recorded_at: firstYesValue === true
        ? targetQuote.first_yes_recorded_at || savedAt
        : null,
      second_yes_recorded_at: (secondYesValue === true || (secondYesValue === null && secondYesConfirmed(targetQuote)))
        ? targetQuote.second_yes_recorded_at || savedAt
        : null,
      third_yes_recorded_at: thirdYesValue === true
        ? targetQuote.third_yes_recorded_at || savedAt
        : null,
      updated_at: savedAt,
    };

    const { error: saveError } = await supabaseClient
      .from('quote_workflow')
      .upsert(payload, { onConflict: 'quote_id' });

    if (saveError) {
      console.error('[SupervisorQuoteOperations] workflow save failed:', saveError);
      setSaveMessage(`Save failed: ${saveError.message}`);
      setSaving(false);
      return;
    }

    const overrideHistoryText = buildOverrideHistoryText({
      outcome,
      followUpAt,
      firstYesValue,
      secondYesValue,
      thirdYesValue,
    });

    const overrideSource =
      cleanStr(profile?.role).toLowerCase() === 'admin'
        ? 'admin_override'
        : 'supervisor_override';

    const { error: overrideHistoryError } = await appendInternalNote(
      targetQuote.id,
      overrideHistoryText,
      overrideSource
    );

    if (overrideHistoryError) {
      console.error('[SupervisorQuoteOperations] override history save failed:', overrideHistoryError);
      setSaveMessage(`Override saved, but history failed: ${overrideHistoryError.message}`);
      setSaving(false);
      await fetchQuotes({ quiet: true });
      return;
    }

    if (agentEntry.notes.trim()) {
      const { error: noteError } = await appendInternalNote(
        targetQuote.id,
        agentEntry.notes,
        cleanStr(profile?.role).toLowerCase() || 'supervisor'
      );
      if (noteError) {
        console.error('[SupervisorQuoteOperations] internal note save failed:', noteError);
        setSaveMessage(`Override saved, but internal note history failed: ${noteError.message}`);
        setSaving(false);
        await fetchQuotes({ quiet: true });
        return;
      }
      setAgentEntry((current) => ({ ...current, notes: '' }));
    }

    await fetchQuotes({ quiet: true });
    setSaveMessage('Saved');
    setSaving(false);
  };

  const appendInternalNote = async (quoteId, noteText, source = 'supervisor') => {
    const cleanNote = String(noteText || '').trim();
    if (!quoteId || !cleanNote) return { error: null };

    const role = cleanStr(profile?.role).toLowerCase() || source;
    const authorName =
      cleanStr(profile?.csr_name) ||
      cleanStr(profile?.full_name) ||
      cleanStr(profile?.name) ||
      cleanStr(profile?.email) ||
      'Internal User';

    return supabaseClient
      .from('quote_internal_notes')
      .insert({
        quote_id: quoteId,
        note_text: cleanNote,
        author_email: profile?.email || null,
        author_name: authorName,
        author_role: role,
        note_source: source || role,
      });
  };

  const clearFilters = () => {
    setSearch('');
    setAgentFilter('all');
  };

  const pageLoading = loading || settingsLoading;

  if (profile && userRole && userRole !== 'supervisor') {
    return (
      <div className={styles.page}>
        <div className={styles.errorBanner}>
          This page is for supervisor accounts.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Supervisor / Office View</p>
          <h1>Quote Operations</h1>
          <p className={styles.subtitle}>
            Manage your office's live quotes, coach the 3 Yes process, recover lost opportunities, and confirm closed business.
          </p>
        </div>

        <div className={styles.headerActions}>
          <div className={styles.timeViewControl}>
            <span>Time View</span>
            <div className={styles.timeViewTabs}>
              {['day', 'week', 'month'].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={timeView === mode ? styles.timeViewTabActive : ''}
                  onClick={() => setTimeView(mode)}
                >
                  {mode}
                </button>
              ))}
            </div>
            <div className={styles.rangeNavigator}>
              <button
                type="button"
                aria-label="Previous period"
                onClick={() => {
                  const next = shiftRange(timeView, selectedDate, month, -1);
                  setSelectedDate(next.date);
                  setMonth(next.month);
                }}
              >‹</button>
              {timeView === 'month' ? (
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
              ) : (
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setMonth(monthValue(parseLocalDate(e.target.value)));
                  }}
                />
              )}
              <button
                type="button"
                aria-label="Next period"
                onClick={() => {
                  const next = shiftRange(timeView, selectedDate, month, 1);
                  setSelectedDate(next.date);
                  setMonth(next.month);
                }}
              >›</button>
            </div>
            <strong className={styles.rangeLabel}>{rangeLabel(timeView, selectedDate, month)}</strong>
          </div>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={refreshAll}
            disabled={pageLoading}
          >
            {pageLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </header>

      {error && <div className={styles.errorBanner}>{error}</div>}

      <section className={`${styles.scopeBar} ${styles.supervisorScopeBar}`}>
        <div className={styles.scopeTitle}>
          <span>Viewing Office</span>
          <strong>{scopeLabel}</strong>
        </div>

        <label>
          <span>Agent</span>
          <select value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)}>
            <option value="all">All Agents</option>
            {agents.map((agent) => (
              <option key={agent} value={agent}>{agent}</option>
            ))}
          </select>
        </label>

        <label className={styles.scopeSearch}>
          <span>Search</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Customer, quote, phone, carrier..."
          />
        </label>

        <button type="button" className={styles.clearButton} onClick={clearFilters}>
          Clear
        </button>
      </section>

      <section className={styles.statsGrid}>
        <DashboardStat
          label="Quoting Now"
          value={stats.live}
          note={`${stats.activeAgents} active agents · ${profileOffice}`}
          tone="live"
        />
        <DashboardStat
          label="Needs Attention"
          value={stats.attention}
          note="1st / 2nd / 3rd Yes + follow-up"
          tone="attention"
        />
        <DashboardStat
          label="Quotes"
          value={stats.quotes}
          note={`${newQuoteGroups.length} new · ${existingClientQuoteGroups.length} existing`}
        />
        <DashboardStat
          label="Closed Deals"
          value={stats.closed}
          note={`${newClosedGroups.length} new · ${existingClientClosedGroups.length} existing`}
          tone="closed"
        />
      </section>

      <nav className={styles.tabBar} aria-label="Quote operations views">
        {TABS.map(([value, label]) => {
          const count = value === 'live'
            ? stats.live
            : value === 'attention'
              ? stats.attention
              : value === 'quotes'
                ? stats.quotes
                : value === 'followups'
                  ? stats.followups
                  : value === 'calendar'
                    ? stats.calendarFollowups
                    : value === 'closed'
                  ? stats.closed
                  : value === 'office_access'
                    ? officeLogAccessCount
                    : stats.customers;

          return (
            <button
              key={value}
              type="button"
              className={`${styles.tabButton} ${activeTab === value ? styles.tabButtonActive : ''}`}
              onClick={() => setActiveTab(value)}
            >
              <span>{label}</span>
              <strong>{count}</strong>
            </button>
          );
        })}
      </nav>

      {activeTab === 'live' && myActiveGroup && (
        <div className={styles.myActiveQuoteSection}>
          <div className={styles.myActiveQuoteHeading}>
            <div>
              <span>YOUR QUOTE</span>
              <strong>Complete the 3 Yes workflow while you quote.</strong>
            </div>
            {myLiveGroups.length > 1 && (
              <span>{myLiveGroups.length} of your quotes are currently live</span>
            )}
          </div>

          <QuoteSalesFlow
            group={myActiveGroup}
            mode="supervisor"
            onSaved={() => fetchQuotes({ quiet: true })}
            onViewDetails={() => openCustomer(myActiveGroup)}
          />
        </div>
      )}

      {activeTab === 'live' && (
        <LiveActivityView
          groups={liveGroups}
          grouped={liveByRegion}
          now={now}
          onOpen={openCustomer}
          quoteEventsByQuote={quoteEventsByQuote}
          internalNotesByQuote={internalNotesByQuote}
          viewerRole="supervisor"
        />
      )}

      {activeTab === 'attention' && (
        <div className={styles.bucketSplitStack}>
          <NeedsAttentionView
            title="Lost Deals"
            description="Not-closed deals where management was contacted for Deal Save help / a lower broker fee, but the customer still did not close."
            items={lostDealAttention}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No Lost Deals in this view"
            emptyDetail="Lost Deals will appear here after the final Not Closed outcome is recorded."
          />
          <NeedsAttentionView
            title="Walks"
            description="Not-closed deals where the customer left on price before the agent contacted management for Deal Save help."
            items={walkAttention}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No Walks in this view"
            emptyDetail="Walks will appear here after the final Not Closed outcome is recorded."
          />
          <NeedsAttentionView
            title="Carrier Bridge Opportunities"
            description="Verified carrier launches that still need a final outcome or a completed return to Agency Matrix."
            items={carrierBridgeAttention}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No unresolved carrier bridges"
            emptyDetail="v0.11.7+ carrier launches will appear here when the agent does not finish the quote workflow."
          />
          <NeedsAttentionView
            title="Unresolved 3 Yes Opportunities"
            description="Quotes that reached 1st, 2nd, or 3rd Yes but never received a final outcome. Includes stale quotes and completed Matrix returns that were never dispositioned."
            items={unresolvedYesAttention}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No unresolved 3 Yes opportunities"
            emptyDetail="Any quote that reaches the 3 Yes process and then goes stale or bridges back without a final outcome will appear here."
          />
        </div>
      )}

      {activeTab === 'quotes' && (
        <div className={styles.bucketSplitStack}>
          <QuoteBucketView
            title="New Quotes"
            description="New prospects that remained regular quotes and did not progress into the 3 Yes process."
            groups={newQuoteGroups}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No new quotes in this view"
            emptyDetail="Regular new-business quotes will appear here after they stop being live."
            mode="quotes"
          />
          <QuoteBucketView
            title="Payment"
            description="Existing-client re-writes created while reviewing a current-policy payment."
            groups={paymentQuoteGroups}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No payment re-writes in this view"
            emptyDetail="Payment re-writes will appear here."
            mode="quotes"
          />
          <QuoteBucketView
            title="Endorsement Comparison"
            description="Existing-client re-writes created to compare a policy change or endorsement against a new rate."
            groups={endorsementQuoteGroups}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No endorsement comparisons in this view"
            emptyDetail="Endorsement comparison re-writes will appear here."
            mode="quotes"
          />
          <QuoteBucketView
            title="Renewal"
            description="Existing-client re-writes created while reviewing an upcoming or current renewal."
            groups={renewalQuoteGroups}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No renewal quotes in this view"
            emptyDetail="Renewal comparison re-writes will appear here."
            mode="quotes"
          />
          <QuoteBucketView
            title="Reinstatement"
            description="Existing-client re-writes created while evaluating a reinstatement opportunity."
            groups={reinstatementQuoteGroups}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No reinstatement quotes in this view"
            emptyDetail="Reinstatement re-writes will appear here."
            mode="quotes"
          />
          <QuoteBucketView
            title="Re-Write - Policy Within Last 6 Months"
            description="Former clients whose prior policy with us was within the last six months and are being re-quoted."
            groups={recentRewriteQuoteGroups}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No recent-policy re-writes in this view"
            emptyDetail="Recent prior-policy re-writes will appear here."
            mode="quotes"
          />
          {unclassifiedExistingQuoteGroups.length > 0 && (
            <QuoteBucketView
              title="Existing Client - Needs Classification"
              description="Existing-client re-writes that still need one of the four re-write reasons assigned."
              groups={unclassifiedExistingQuoteGroups}
              now={now}
              onOpen={openCustomer}
              emptyTitle="No unclassified existing-client quotes"
              emptyDetail="All existing-client quotes are classified."
              mode="quotes"
            />
          )}
        </div>
      )}

      {activeTab === 'followups' && (
        <SupervisorFollowUpList
          groups={followUpGroups}
          now={now}
          rangeLabelText={rangeLabel(timeView, selectedDate, month)}
          onOpen={openCustomer}
          onBackToCalendar={() => {
            setTimeView('month');
            setActiveTab('calendar');
          }}
        />
      )}

      {activeTab === 'calendar' && (
        <SupervisorFollowUpCalendar
          month={calendarMonth}
          groups={calendarFollowUpGroups}
          onOpen={openCustomer}
          onSelectDay={(date) => {
            setSelectedDate(date);
            setTimeView('day');
            setActiveTab('followups');
          }}
        />
      )}

      {activeTab === 'closed' && (
        <div className={styles.bucketSplitStack}>
          <QuoteBucketView
            title="New Business Closed"
            description="Confirmed sold / policy-bound new-business deals."
            groups={newClosedGroups}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No new-business closes in this view"
            emptyDetail="Closed new-business deals will appear here automatically."
            mode="closed"
          />
          <QuoteBucketView
            title="Payment Closed"
            description="Closed existing-client re-write deals that originated from a payment review."
            groups={paymentClosedGroups}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No closed payment re-writes in this view"
            emptyDetail="Closed payment re-writes will appear here."
            mode="closed"
          />
          <QuoteBucketView
            title="Endorsement Comparison Closed"
            description="Closed re-write deals that originated from an endorsement comparison."
            groups={endorsementClosedGroups}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No closed endorsement comparisons in this view"
            emptyDetail="Closed endorsement comparison re-writes will appear here."
            mode="closed"
          />
          <QuoteBucketView
            title="Renewal Closed"
            description="Closed re-write deals that originated from a renewal review."
            groups={renewalClosedGroups}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No closed renewals in this view"
            emptyDetail="Closed renewal re-writes will appear here."
            mode="closed"
          />
          <QuoteBucketView
            title="Reinstatement Closed"
            description="Closed re-write deals that originated from a reinstatement review."
            groups={reinstatementClosedGroups}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No closed reinstatements in this view"
            emptyDetail="Closed reinstatement re-writes will appear here."
            mode="closed"
          />
          <QuoteBucketView
            title="Re-Write - Policy Within Last 6 Months Closed"
            description="Closed deals from former clients whose prior policy with us was within the last six months."
            groups={recentRewriteClosedGroups}
            now={now}
            onOpen={openCustomer}
            emptyTitle="No closed recent-policy re-writes in this view"
            emptyDetail="Closed recent prior-policy re-writes will appear here."
            mode="closed"
          />
          {unclassifiedExistingClosedGroups.length > 0 && (
            <QuoteBucketView
              title="Existing Client Closed - Needs Classification"
              description="Closed existing-client re-writes that still need one of the four re-write reasons assigned."
              groups={unclassifiedExistingClosedGroups}
              now={now}
              onOpen={openCustomer}
              emptyTitle="No unclassified existing-client closes"
              emptyDetail="All existing-client closed deals are classified."
              mode="closed"
            />
          )}
        </div>
      )}

      {activeTab === 'office_access' && (
        <SupervisorOfficeAccess
          office={profileOffice}
          region={profileRegion || officeRegions[profileOffice] || 'Unassigned'}
          rows={officeDevices}
          loading={officeDevicesLoading}
          message={officeDeviceMessage}
          activeCount={officeLogAccessCount}
          pendingAdminCount={pendingAdminDeviceCount}
          onToggleAccess={setOfficeLogAccess}
          onRefresh={() => fetchOfficeDevices()}
        />
      )}

      {selectedCustomer && (
        <CustomerDrawer
          group={selectedCustomer}
          now={now}
          onClose={() => {
            setSelectedCustomer(null);
            setSaveMessage('');
          }}
          agentEntry={agentEntry}
          setAgentEntry={setAgentEntry}
          saveCustomerOutcome={saveCustomerOutcome}
          saving={saving}
          saveMessage={saveMessage}
          quoteEventsByQuote={quoteEventsByQuote}
          internalNotesByQuote={internalNotesByQuote}
        />
      )}
    </div>
  );
}


function LiveActivityView({ groups, grouped, now, onOpen, quoteEventsByQuote, viewerRole }) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeading}>
        <div>
          <div className={styles.headingWithDot}>
            <span className={styles.liveDot} />
            <h2>Live Activity</h2>
          </div>
          <p>Watch active quotes move through Quote → 1st Yes → 2nd Yes → 3rd Yes in real time.</p>
        </div>
        <span className={styles.countPill}>{groups.length} live</span>
      </div>

      {grouped.length === 0 ? (
        <EmptyState
          title="No live quotes right now"
          detail="As soon as an approved computer starts a quote, it will appear here automatically."
        />
      ) : (
        <div className={styles.liveRegionList}>
          {grouped.map((region) => (
            <div key={region.region} className={styles.liveRegionBlock}>
              {viewerRole === 'admin' && (
                <div className={styles.regionHeader}>
                  <strong>{region.region}</strong>
                  <span>{region.offices.reduce((sum, office) => sum + office.groups.length, 0)} active</span>
                </div>
              )}

              {region.offices.map((office) => (
                <div key={`${region.region}-${office.office}`} className={styles.liveOfficeBlock}>
                  {viewerRole !== 'supervisor' && (
                    <div className={styles.officeLiveHeader}>
                      <strong>{office.office}</strong>
                      <span>{office.groups.length} live</span>
                    </div>
                  )}
                  <div className={styles.liveDealGrid}>
                    {office.groups.map((group) => {
                    const quote = currentQuoteForGroup(group);
                    const stage = quoteStage(quote);
                    const events = reconcileTimelineEvents(quote, quoteEventsByQuote[quote.id] || []);
                    const recentEvents = events.slice(-3).reverse();
                    const drivers = Number(quote.driver_count || 0);
                    const licensed = Number(quote.drivers_with_license || 0);
                    const vehicles = Number(quote.vehicle_count || 0);
                    const fullVins = Number(quote.vehicles_with_full_vin || 0);

                    return (
                      <article key={group.key} className={`${styles.liveDealCard} ${styles[`liveDealCard_${stage.tone}`] || ''}`}>
                        <div className={styles.liveDealTop}>
                          <div>
                            <span className={styles.liveNowLabel}>● LIVE · {elapsedLabel(quote.started_at || group.lastActivityAt, now)}</span>
                            <h3>{group.customerName}</h3>
                            <p>{group.agentName} · {group.office}</p>
                            <div className={styles.liveContextLine}>
                              <span>{contactMethodLabel(quote)}</span>
                              <span>·</span>
                              <span>{quoteBusinessTypeLabel(quote)}</span>
                              {inferQuoteBusinessType(quote) === 'existing_client' && (
                                <>
                                  <span>·</span>
                                  <strong>{existingClientReasonLabel(quote)}</strong>
                                </>
                              )}
                              {group.reopenedLostDeal && (
                                <>
                                  <span>·</span>
                                  <strong>RE-OPENED LOST DEAL</strong>
                                </>
                              )}
                            </div>
                          </div>
                          <span className={`${styles.intentBadge} ${styles[`intentBadge_${stage.tone}`] || ''}`}>
                            {stage.label}
                          </span>
                          {quote.office_mismatch && (
                            <span
                              className={styles.officeMismatchBadge}
                              title={`TurboRater office ${quote.turborater_office || 'unknown'} differs from trusted-device office ${quote.office || group.office}`}
                            >
                              Office Mismatch
                            </span>
                          )}
                        </div>

                        <div className={styles.liveSignalGrid}>
                          <div>
                            <span>Drivers</span>
                            <strong>{drivers ? `${licensed}/${drivers}` : '—'}</strong>
                            <small>with license</small>
                          </div>
                          <div>
                            <span>Vehicles</span>
                            <strong>{vehicles ? `${fullVins}/${vehicles}` : '—'}</strong>
                            <small>full VIN</small>
                          </div>
                          <div>
                            <span>Last activity</span>
                            <strong>{elapsedLabel(quote.last_live_activity_at || group.lastActivityAt, now)}</strong>
                            <small>ago</small>
                          </div>
                        </div>

                        <div className={styles.liveTimelineMini}>
                          {recentEvents.length === 0 ? (
                            <span className={styles.mutedText}>Waiting for the next 3 Yes milestone…</span>
                          ) : recentEvents.map((event) => (
                            <div key={event.id || `${event.event_type}-${event.event_at}`}>
                              <time>{formatDateTime(event.event_at)}</time>
                              <span>{eventDisplay(event)}</span>
                            </div>
                          ))}
                        </div>

                        <div className={styles.liveDealFooter}>
                          <span>
                            {quote.carrier || 'Carrier not selected yet'}
                            {quote.monthly_payment ? ` · ${formatMoney(quote.monthly_payment)}/mo` : ''}
                          </span>
                          <button type="button" className={styles.secondaryButton} onClick={() => onOpen(group)}>
                            View Deal
                          </button>
                        </div>
                      </article>
                    );
                  })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function NeedsAttentionView({
  items,
  now,
  onOpen,
  title = 'Needs Attention',
  description = 'Not-closed deals that need management review.',
  emptyTitle = 'Nothing needs attention',
  emptyDetail = 'Not-closed deals will appear here when they match this category.',
}) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeading}>
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <span className={`${styles.countPill} ${styles.attentionCountPill}`}>{items.length}</span>
      </div>

      {items.length === 0 ? (
        <EmptyState
          title={emptyTitle}
          detail={emptyDetail}
        />
      ) : (
        <div className={styles.attentionDealList}>
          {items.map(({ group, attention }) => {
            const quote = group.stageQuote || currentQuoteForGroup(group);
            const drivers = Number(quote.driver_count || 0);
            const licensed = Number(quote.drivers_with_license || 0);
            const vehicles = Number(quote.vehicle_count || 0);
            const fullVins = Number(quote.vehicles_with_full_vin || 0);
            const yesStage = Math.max(0, Math.min(3, Number(group.yesStage || threeYesStage(quote) || 0)));
            const followUpAt = groupFollowUpAt(group);
            const latestNote = latestGroupNote(group);

            return (
              <article key={group.key} className={`${styles.attentionDealCard} ${styles[`attentionDealCard_${attention.level}`] || ''}`}>
                <div className={styles.attentionDealIdentity}>
                  <span className={styles.attentionPriorityLabel}>{attention.stage ? `${attention.stage}${attention.stage === 1 ? 'st' : attention.stage === 2 ? 'nd' : 'rd'} Yes` : 'Priority'}</span>
                  <h3>{group.customerName}</h3>
                  <p>{group.agentName} · {group.region} · {group.office}</p>
                  <small>Last activity {elapsedLabel(group.lastActivityAt, now)} ago</small>
                </div>

                <div className={styles.attentionReason}>
                  <strong>{attention.title}</strong>
                  <span>{attention.detail}</span>
                  <div className={styles.intentSignalsInline}>
                    <span className={licensed > 0 ? styles.signalPositive : styles.signalNeutral}>
                      {licensed > 0 ? '✓' : '—'} {drivers ? `${licensed}/${drivers}` : '0'} license{drivers === 1 ? '' : 's'}
                    </span>
                    <span className={fullVins > 0 ? styles.signalPositive : styles.signalNeutral}>
                      {fullVins > 0 ? '✓' : '—'} {vehicles ? `${fullVins}/${vehicles}` : '0'} full VIN{vehicles === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className={styles.intentSignalsInline}>
                    <span><strong>3 Yes:</strong> {yesStage}/3</span>
                    <span><strong>Follow-up:</strong> {followUpAt ? formatDateTime(followUpAt) : 'Not entered'}</span>
                  </div>
                  <span className={styles.subCell}>
                    <strong>Latest note:</strong> {latestNote?.text ? briefText(latestNote.text, 110) : 'No note entered'}
                  </span>
                </div>

                <div className={styles.attentionDealQuote}>
                  <span>{quote.carrier || 'No carrier recorded'}</span>
                  <strong>{formatMoney(quote.monthly_payment)} / mo</strong>
                  <small>{quote.bridge_policy_status || 'No closing reason listed'}</small>
                </div>

                <div className={styles.attentionActionBox}>
                  <strong>Recommended action</strong>
                  <span>Review the deal stage, closing reason, and customer follow-up.</span>
                  <button type="button" className={styles.primarySmallButton} onClick={() => onOpen(group)}>
                    Review Deal
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}


function SupervisorFollowUpList({ groups, now, rangeLabelText, onOpen, onBackToCalendar }) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeading}>
        <div className={styles.followUpHeadingWithBack}>
          <button type="button" className={styles.backToCalendarButton} onClick={onBackToCalendar}>← Back to Calendar</button>
          <div>
            <h2>Scheduled Follow-Ups</h2>
            <p>{rangeLabelText} · Appointments and callbacks scheduled by agents in this office.</p>
          </div>
        </div>
        <span className={`${styles.countPill} ${styles.followUpCountPill}`}>{groups.length} scheduled</span>
      </div>

      {groups.length === 0 ? (
        <EmptyState title="No follow-ups in this view" detail="Scheduled follow-ups for this office will appear here." />
      ) : (
        <div className={styles.bucketTableWrap}>
          <table className={styles.bucketTable}>
            <thead><tr><th>Follow-Up</th><th>Customer</th><th>Agent</th><th>Contact</th><th>Quote</th><th>Last Activity</th><th></th></tr></thead>
            <tbody>
              {groups.map((group) => {
                const quote = currentQuoteForGroup(group);
                const followUpAt = groupFollowUpAt(group);
                const pastDue = followUpAt && new Date(followUpAt).getTime() < now;
                return (
                  <tr key={group.key}>
                    <td><strong className={pastDue ? styles.followUpPastDue : ''}>{formatDateTime(followUpAt)}</strong><span className={styles.subCell}>{pastDue ? 'Past due' : 'Scheduled'}</span></td>
                    <td><strong>{group.customerName}</strong><span className={styles.subCell}>Customer #{group.matrixCustomerId || 'N/A'}</span></td>
                    <td>{group.agentName}</td>
                    <td>{group.phone || group.email || '—'}</td>
                    <td>{quote.matrix_quote_id || '—'}<span className={styles.subCell}>{quote.carrier || 'No carrier selected'}</span></td>
                    <td>{formatDateTime(group.lastActivityAt)}<span className={styles.subCell}>{elapsedLabel(group.lastActivityAt, now)} ago</span></td>
                    <td><button type="button" className={styles.secondaryButton} onClick={() => onOpen(group)}>Open</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SupervisorFollowUpCalendar({ month, groups, onOpen, onSelectDay }) {
  const [year, monthNumber] = month.split('-').map(Number);
  const first = new Date(year, monthNumber - 1, 1);
  const daysInMonth = new Date(year, monthNumber, 0).getDate();
  const leading = first.getDay();
  const monthLabel = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(first);

  const byDay = new Map();
  groups.forEach((group) => {
    const key = localDateKey(groupFollowUpAt(group));
    if (!key) return;
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(group);
  });
  byDay.forEach((rows) => rows.sort((a, b) => new Date(groupFollowUpAt(a)) - new Date(groupFollowUpAt(b))));

  const cells = [];
  for (let i = 0; i < leading; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeading}>
        <div><h2>Follow-Up Calendar</h2><p>{monthLabel} · All scheduled follow-ups for this office. Click a day to open that day's list.</p></div>
        <span className={`${styles.countPill} ${styles.followUpCountPill}`}>{groups.length} appointments</span>
      </div>
      <div className={styles.followUpCalendar}>
        <div className={styles.calendarWeekdays}>{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((day) => <strong key={day}>{day}</strong>)}</div>
        <div className={styles.calendarGrid}>
          {cells.map((day, index) => {
            if (!day) return <div key={`blank-${index}`} className={`${styles.calendarDay} ${styles.calendarDayBlank}`} />;
            const key = `${year}-${String(monthNumber).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
            const appointments = byDay.get(key) || [];
            return (
              <div key={key} className={`${styles.calendarDay} ${appointments.length ? styles.calendarDayHasAppointments : ''}`}>
                <button type="button" className={styles.calendarDayHeader} onClick={() => onSelectDay(key)}><span>{day}</span>{appointments.length > 0 && <strong>{appointments.length}</strong>}</button>
                <div className={styles.calendarAppointments}>
                  {appointments.slice(0,3).map((group) => (
                    <button key={group.key} type="button" className={styles.calendarAppointment} onClick={() => onOpen(group)}>
                      <time>{new Intl.DateTimeFormat('en-US',{hour:'numeric',minute:'2-digit'}).format(new Date(groupFollowUpAt(group)))}</time>
                      <strong>{group.customerName}</strong>
                      <span>{group.agentName}</span>
                    </button>
                  ))}
                  {appointments.length > 3 && <button type="button" className={styles.calendarMore} onClick={() => onSelectDay(key)}>View {appointments.length - 3} more appointments →</button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function QuoteBucketView({
  title,
  description,
  groups,
  now,
  onOpen,
  emptyTitle,
  emptyDetail,
  mode,
}) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeading}>
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <span className={`${styles.countPill} ${mode === 'closed' ? styles.closedCountPill : ''}`}>{groups.length}</span>
      </div>

      {groups.length === 0 ? (
        <EmptyState title={emptyTitle} detail={emptyDetail} />
      ) : (
        <div className={styles.bucketTableWrap}>
          <table className={styles.bucketTable}>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Region / Office</th>
                <th>Agent</th>
                <th>{mode === 'closed' ? 'Carrier' : 'At a Glance'}</th>
                <th>{mode === 'closed' ? 'Monthly' : 'Status'}</th>
                <th>Last Activity</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => {
                const quote = currentQuoteForGroup(group);
                const drivers = Number(quote.driver_count || 0);
                const licensed = Number(quote.drivers_with_license || 0);
                const vehicles = Number(quote.vehicle_count || 0);
                const fullVins = Number(quote.vehicles_with_full_vin || 0);
                const yesStage = Math.max(0, Math.min(3, Number(group.yesStage || threeYesStage(quote) || 0)));
                const followUpAt = groupFollowUpAt(group);
                const latestNote = latestGroupNote(group);

                return (
                  <tr key={group.key}>
                    <td>
                      <strong>{group.customerName}</strong>
                      <span className={styles.subCell}>Customer #{group.matrixCustomerId || 'N/A'}</span>
                    </td>
                    <td>
                      <strong>{group.office}</strong>
                      <span className={styles.subCell}>{group.region}</span>
                    </td>
                    <td>{group.agentName}</td>
                    <td>
                      {mode === 'closed' ? (
                        <>
                          <strong>{quote.carrier || '—'}</strong>
                          <span className={styles.subCell}>{formatMoney(quote.total_premium)} premium</span>
                        </>
                      ) : (
                        <>
                          <strong>3 Yes: {yesStage}/3</strong>
                          <span className={styles.subCell}>
                            ID / VIN: {licensed}/{drivers || 0} DL · {fullVins}/{vehicles || 0} VIN
                          </span>
                          <span className={styles.subCell}>
                            Follow-up: {followUpAt ? formatDateTime(followUpAt) : 'Not entered'}
                          </span>
                          <span className={styles.subCell} title={latestNote?.text || ''}>
                            Note: {latestNote?.text ? briefText(latestNote.text) : 'No note entered'}
                          </span>
                        </>
                      )}
                    </td>
                    <td>
                      {mode === 'closed' ? (
                        <strong>{formatMoney(quote.monthly_payment)}</strong>
                      ) : (
                        <span className={styles.lowIntentBadge}>
                          {isDidNotRewriteGroup(group)
                            ? 'Stayed With Current Carrier'
                            : quote.bridge_policy_status || (getDisplayStatus(quote, now) === 'stale' ? 'Incomplete / stale' : 'Quote')}
                        </span>
                      )}
                    </td>
                    <td>
                      {formatDateTime(group.lastActivityAt)}
                      <span className={styles.subCell}>{elapsedLabel(group.lastActivityAt, now)} ago</span>
                    </td>
                    <td>
                      <button type="button" className={styles.secondaryButton} onClick={() => onOpen(group)}>
                        Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function CustomerDrawer({
  group,
  now,
  onClose,
  agentEntry,
  setAgentEntry,
  saveCustomerOutcome,
  saving,
  saveMessage,
  quoteEventsByQuote,
  internalNotesByQuote,
}) {
  const latest = group.quotes[0] || {};
  const drivers = safeArray(latest.drivers_summary);
  const vehicles = safeArray(latest.vehicles_summary);
  const events = reconcileTimelineEvents(latest, quoteEventsByQuote?.[latest.id] || []);
  const internalNotes = group.quotes
    .flatMap((quote) => internalNotesByQuote?.[quote.id] || [])
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  const stage = quoteStage(latest);
  const licensed = Number(latest.drivers_with_license || 0);
  const driverCount = Number(latest.driver_count || drivers.length || 0);
  const fullVins = Number(latest.vehicles_with_full_vin || 0);
  const vehicleCount = Number(latest.vehicle_count || vehicles.length || 0);
  const completedAt = latest.bridged_back_at || latest.updated_at || null;

  return (
    <div className={styles.drawerBackdrop} role="presentation" onMouseDown={onClose}>
      <aside className={`${styles.drawer} ${styles.drawerWide}`} onMouseDown={(event) => event.stopPropagation()}>
        <div className={styles.drawerHeader}>
          <div>
            <p className={styles.eyebrow}>Customer Quote Details</p>
            <h2>{group.customerName}</h2>
            <p>{group.agentName} · {group.region} · {group.office}</p>
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close details">×</button>
        </div>

        <div className={styles.drawerBody}>
          <section className={styles.dealHero}>
            <div className={styles.dealHeroIdentity}>
              <div>
                <span>Customer</span>
                <strong>{group.customerName}</strong>
              </div>
              <div>
                <span>Phone</span>
                <strong>{group.phone || latest.phone || '—'}</strong>
              </div>
              <div>
                <span>Customer #</span>
                <strong>{latest.matrix_customer_id || '—'}</strong>
              </div>
              <div>
                <span>Quote #</span>
                <strong>{latest.matrix_quote_id || '—'}</strong>
              </div>
              <div>
                <span>Rater #</span>
                <strong>{latest.rater_id || '—'}</strong>
              </div>
              <div>
                <span>Lead Source</span>
                <strong>{latest.lead_source || '—'}</strong>
              </div>
            </div>

            <div className={`${styles.threeYesBanner} ${styles[`threeYesBanner_${stage.tone}`] || ''}`}>
              <div>
                <span>3 Yes Deal Stage</span>
                <strong>{stage.label}</strong>
                <small>{threeYesEvidenceLabel(latest)}</small>
                <small>{quoteBusinessTypeLabel(latest)}{inferQuoteBusinessType(latest) === 'existing_client' ? ` · ${existingClientReasonLabel(latest)}` : ''}</small>
              </div>
              <div className={styles.threeYesSteps}>
                <span className={firstYesConfirmed(latest) ? styles.yesStepOn : styles.yesStepOff}>1st Yes</span>
                <span className={secondYesConfirmed(latest) ? styles.yesStepOn : styles.yesStepOff}>2nd Yes</span>
                <span className={thirdYesConfirmed(latest) ? styles.yesStepOn : styles.yesStepOff}>3rd Yes</span>
              </div>
            </div>
          </section>

          <section className={styles.drawerSection}>
            <div className={styles.drawerSectionHeading}>
              <div>
                <h3>Drivers</h3>
                <p>{licensed}/{driverCount || 0} with license entered. License numbers are never displayed.</p>
              </div>
            </div>
            <div className={styles.driverGrid}>
              <div className={styles.driverGridHeader}>
                <span>Driver</span>
                <span>DOB</span>
                <span>Type</span>
                <span>License Entered</span>
              </div>

              {drivers.map((driver) => (
                <div className={styles.driverGridRow} key={driver.index || driver.name}>
                  <strong>{driver.name || `Driver ${driver.index || ''}`}</strong>
                  <span>{driver.dob || '—'}</span>
                  <span>{driver.driverType || '—'}</span>
                  <span>
                    {driver.licenseEntered ? (
                      <span className={styles.enteredBadge}>✓ Entered</span>
                    ) : (
                      <span className={styles.notEnteredBadge}>—</span>
                    )}
                  </span>
                </div>
              ))}

              {drivers.length === 0 && (
                <div className={styles.detailGridEmpty}>No driver snapshot captured for this quote.</div>
              )}
            </div>
          </section>

          <section className={styles.drawerSection}>
            <div className={styles.drawerSectionHeading}>
              <div>
                <h3>Vehicles</h3>
                <p>{fullVins}/{vehicleCount || 0} with a full 17-character VIN. Actual VINs are never displayed.</p>
              </div>
            </div>
            <div className={styles.vehicleGrid}>
              <div className={styles.vehicleGridHeader}>
                <span>Vehicle</span>
                <span>VIN Status</span>
              </div>

              {vehicles.map((vehicle) => (
                <div className={styles.vehicleGridRow} key={vehicle.index || `${vehicle.year}-${vehicle.make}-${vehicle.model}`}>
                  <strong>
                    {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') || `Vehicle ${vehicle.index || ''}`}
                  </strong>
                  <span>
                    {vehicle.fullVinEntered ? (
                      <span className={styles.enteredBadge}>✓ Full VIN entered</span>
                    ) : vehicle.vinPresent ? (
                      <span className={styles.partialVinBadge}>Partial / ITC VIN</span>
                    ) : (
                      <span className={styles.notEnteredBadge}>—</span>
                    )}
                  </span>
                </div>
              ))}

              {vehicles.length === 0 && (
                <div className={styles.detailGridEmpty}>No vehicle snapshot captured for this quote.</div>
              )}
            </div>
          </section>

          <section className={styles.drawerSection}>
            <div className={styles.drawerSectionHeading}>
              <div>
                <h3>Quote & Pricing</h3>
                <p>Current carrier, pricing, lifecycle, and captured TurboRater status.</p>
                {latest.office_mismatch && (
                  <div className={styles.officeMismatchNotice}>
                    <strong>Office mismatch detected</strong>
                    <span>
                      TurboRater showed {latest.turborater_office || 'an unknown office'}, but this quote is assigned to
                      {' '}{latest.office || group.office} from the trusted extension device.
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.quoteDetailGrid}>
              <BridgeField label="Agent" value={group.agentName} />
              <BridgeField label="Operational Office" value={latest.office || group.office} />
              <BridgeField
                label="Office Source"
                value={
                  latest.office_source === 'trusted_device'
                    ? 'Trusted Device'
                    : latest.office_source === 'legacy_turborater'
                      ? 'Legacy TurboRater'
                      : latest.office_source || '—'
                }
              />
              <BridgeField label="TurboRater Office" value={latest.turborater_office || '—'} />
              <BridgeField
                label="Office Match"
                value={
                  latest.office_mismatch
                    ? `⚠ Mismatch — routed to ${latest.office || group.office}`
                    : latest.office_source === 'trusted_device'
                      ? '✓ Matches trusted device'
                      : '—'
                }
              />
              <BridgeField label="Contact Method" value={contactMethodLabel(latest)} />
              <BridgeField label="Quote Type" value={quoteBusinessTypeLabel(latest)} />
              <BridgeField label="3 Yes Stage" value={stage.label} />
              <BridgeField label="Carrier" value={latest.carrier} />
              <BridgeField label="Premium" value={formatMoney(latest.total_premium)} />
              <BridgeField label="Down Payment" value={formatMoney(latest.down_payment)} />
              <BridgeField label="Monthly Payment" value={formatMoney(latest.monthly_payment)} />
              <BridgeField label="Started" value={formatDateTime(latest.started_at)} />
              <BridgeField label="Completed" value={formatDateTime(completedAt)} />
              <BridgeField label="Quote Status" value={String(latest.status || '—').toUpperCase()} />
              <BridgeField label="TurboRater Policy Status" value={latest.bridge_policy_status || 'Reason not listed'} />
              <BridgeField label="Sync" value="SYNCED" />
              <BridgeField label="Supabase Quote" value={latest.id} mono />
            </div>
          </section>

          <section className={styles.drawerSection}>
            <div className={styles.drawerSectionHeading}>
              <div>
                <h3>Notes & Internal History</h3>
                <p>Quote notes plus the running agent / supervisor / admin note history.</p>
              </div>
            </div>

            {latest.captured_note && (
              <div className={styles.quoteNotesCard}>
                <span className={styles.noteSourceLabel}>TurboRater Quote Note</span>
                <strong>{latest.captured_note}</strong>
                <div>
                  <span>Entered: {latest.note_entered_at || '—'}</span>
                  <span>Entered By: {latest.note_entered_by || '—'}</span>
                </div>
              </div>
            )}

            <div className={styles.internalNoteHistory}>
              {internalNotes.map((note) => (
                <article key={note.id} className={styles.internalNoteItem}>
                  <div className={styles.internalNoteMeta}>
                    <strong>{note.author_name || note.author_email || 'Internal User'}</strong>
                    <span className={styles.internalRoleBadge}>
                      {note.note_source === 'supervisor_override'
                        ? 'Supervisor Override'
                        : note.note_source === 'admin_override'
                          ? 'Admin Override'
                          : cleanStr(note.author_role || note.note_source || 'internal').replaceAll('_', ' ')}
                    </span>
                    <time>{formatDateTime(note.created_at)}</time>
                  </div>
                  <p>{note.note_text}</p>
                </article>
              ))}

              {internalNotes.length === 0 && !latest.captured_note && (
                <div className={styles.emptyState}>
                  <strong>No notes yet</strong>
                  <span>New internal notes will appear here in chronological history.</span>
                </div>
              )}
            </div>
          </section>

          <section className={styles.drawerSection}>
            <div className={styles.drawerSectionHeading}>
              <div>
                <h3>Deal Timeline</h3>
                <p>Business milestones only. Temporary duplicated ITC vehicle/driver events are reconciled against the final snapshot.</p>
              </div>
            </div>
            <div className={styles.dealTimeline}>
              {events.map((event) => (
                <div key={event.id || `${event.event_type}-${event.event_at}`} className={styles.timelineRow}>
                  <span className={styles.timelineDot} />
                  <time>{formatDateTime(event.event_at)}</time>
                  <strong>{eventDisplay(event)}</strong>
                </div>
              ))}
              {events.length === 0 && <div className={styles.emptyState}><strong>No timeline events yet</strong><span>New v0.11 quotes will build a milestone timeline automatically.</span></div>}
            </div>
          </section>

          <details className={styles.supervisorTools}>
            <summary>Supervisor tools</summary>
            <div className={styles.supervisorToolsBody}>
              <p>Use only when an administrative override or follow-up note is needed. Agent-side disposition remains the preferred source.</p>
              <form className={styles.outcomeForm} onSubmit={saveCustomerOutcome}>
                <label>
                  <span>Quote Type</span>
                  <select
                    value={agentEntry.business_type}
                    onChange={(e) => setAgentEntry((current) => ({
                      ...current,
                      business_type: e.target.value,
                      existing_client_reason: e.target.value === 'existing_client' ? current.existing_client_reason : '',
                    }))}
                  >
                    {QUOTE_BUSINESS_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </label>

                {agentEntry.business_type === 'existing_client' && (
                  <label>
                    <span>Existing Client Type</span>
                    <select value={agentEntry.existing_client_reason} onChange={(e) => setAgentEntry((current) => ({ ...current, existing_client_reason: e.target.value }))}>
                      <option value="">Select type…</option>
                      {EXISTING_CLIENT_REASONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                )}

                <label>
                  <span>1st Yes - Ready to Start Now?</span>
                  <select value={agentEntry.first_yes} onChange={(e) => setAgentEntry((current) => ({ ...current, first_yes: e.target.value }))}>
                    <option value="">Not recorded</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  <small>If we find a price they like, are they ready to start the policy right now?</small>
                </label>

                <label>
                  <span>2nd Yes - ID / License + VIN</span>
                  <select value={agentEntry.second_yes} onChange={(e) => setAgentEntry((current) => ({ ...current, second_yes: e.target.value }))}>
                    <option value="auto">Use system detection</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  <small>System detection currently requires at least one license plus one full 17-character VIN.</small>
                </label>

                <label>
                  <span>3rd Yes - Payment Method</span>
                  <select value={agentEntry.payment_method} onChange={(e) => setAgentEntry((current) => ({ ...current, payment_method: e.target.value }))}>
                    <option value="">Not recorded</option>
                    {PAYMENT_METHODS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  <small>Cash or Card = 3rd Yes. Neither keeps the customer at quote / follow-up stage.</small>
                </label>

                <label>
                  <span>Outcome</span>
                  <select
                    value={agentEntry.status}
                    onChange={(e) => setAgentEntry((current) => ({
                      ...current,
                      status: e.target.value,
                      reason: e.target.value === 'not_closed' ? current.reason : '',
                      follow_up_at: e.target.value === 'follow_up' ? current.follow_up_at : '',
                    }))}
                  >
                    <option value="">Select outcome…</option>
                    <option value="closed">Closed</option>
                    <option value="not_closed">Not Closed</option>
                    <option value="follow_up">Follow Up</option>
                  </select>
                </label>

                {agentEntry.status === 'not_closed' && (
                  <label>
                    <span>Why Not Closed</span>
                    <select value={agentEntry.reason} onChange={(e) => setAgentEntry((current) => ({ ...current, reason: e.target.value }))}>
                      <option value="">Select reason…</option>
                      {NOT_CLOSED_REASONS
                        .filter(([value]) => value !== 'did_not_rewrite_current_carrier')
                        .map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      {agentEntry.business_type === 'existing_client' && agentEntry.existing_client_reason && (
                        <option value="did_not_rewrite_current_carrier">Did Not RW - Stayed With Current Carrier</option>
                      )}
                    </select>
                  </label>
                )}

                {agentEntry.status === 'follow_up' && (
                  <label>
                    <span>Follow-Up Date / Time</span>
                    <input type="datetime-local" value={agentEntry.follow_up_at} onChange={(e) => setAgentEntry((current) => ({ ...current, follow_up_at: e.target.value }))} />
                  </label>
                )}

                <label className={styles.fullWidthField}>
                  <span>Add Internal Note</span>
                  <textarea rows="3" value={agentEntry.notes} onChange={(e) => setAgentEntry((current) => ({ ...current, notes: e.target.value }))} placeholder="Add a new internal note. Previous notes are preserved in history..." />
                </label>

                <div className={styles.formActions}>
                  {saveMessage && <span className={saveMessage.startsWith('Save failed') ? styles.saveError : styles.saveSuccess}>{saveMessage}</span>}
                  <button type="submit" className={styles.primaryButton} disabled={saving}>{saving ? 'Saving…' : 'Save Override'}</button>
                </div>
              </form>
            </div>
          </details>

          {group.quotes.length > 1 && (
            <section className={styles.drawerSection}>
              <div className={styles.drawerSectionHeading}>
                <div>
                  <h3>Other Quote Attempts</h3>
                  <p>{group.quotes.length - 1} additional quote record{group.quotes.length === 2 ? '' : 's'} for this customer.</p>
                </div>
              </div>
              <div className={styles.bridgeList}>
                {group.quotes.slice(1).map((quote) => (
                  <details key={quote.id} className={styles.bridgeItem}>
                    <summary>
                      <div><strong>{quote.carrier || 'Quote attempt'}</strong><span>{formatDateTime(quote.started_at)} · {formatMoney(quote.monthly_payment)}/mo</span></div>
                      <QuoteLifecycleBadge status={getDisplayStatus(quote, now)} />
                    </summary>
                    <div className={styles.bridgeGrid}>
                      <BridgeField label="Carrier" value={quote.carrier} />
                      <BridgeField label="Premium" value={formatMoney(quote.total_premium)} />
                      <BridgeField label="Monthly Payment" value={formatMoney(quote.monthly_payment)} />
                      <BridgeField label="TurboRater Status" value={quote.bridge_policy_status} />
                      <BridgeField label="Contact Method" value={contactMethodLabel(quote)} />
                      <BridgeField label="Quote Type" value={quoteBusinessTypeLabel(quote)} />
                      <BridgeField label="Operational Office" value={quote.office || group.office} />
                      <BridgeField label="TurboRater Office" value={quote.turborater_office || '—'} />
                      <BridgeField label="Matrix Quote ID" value={quote.matrix_quote_id} mono />
                    </div>
                  </details>
                ))}
              </div>
            </section>
          )}
        </div>
      </aside>
    </div>
  );
}

function SupervisorOfficeAccess({
  office,
  region,
  rows,
  loading,
  message,
  activeCount,
  pendingAdminCount,
  onToggleAccess,
  onRefresh,
}) {
  const sortedRows = [...rows].sort((a, b) => {
    const aReady = a.approval_status === 'approved' && a.is_active === true ? 0 : 1;
    const bReady = b.approval_status === 'approved' && b.is_active === true ? 0 : 1;
    if (aReady !== bReady) return aReady - bReady;
    return String(a.device_name || '').localeCompare(String(b.device_name || ''));
  });

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeading}>
        <div>
          <span className={styles.sectionKicker}>Office device access</span>
          <h2>{office} Office Log Access</h2>
          <p>
            Choose which Admin-approved computers can open this office's quote log.
            Device trust and activation remain controlled by Admin.
          </p>
        </div>

        <div className={styles.officeAccessSummary}>
          <span className={styles.officeAccessActive}>{activeCount} with log access</span>
          {pendingAdminCount > 0 && (
            <span className={styles.officeAccessPending}>{pendingAdminCount} waiting on Admin</span>
          )}
        </div>
      </div>

      <div className={styles.officeAccessNotice}>
        <strong>{office} · {region}</strong>
        <span>
          Supervisors cannot approve, reject, activate, disable, or move trusted devices.
          You can only allow or remove Office Log Access after Admin has approved the device.
        </span>
      </div>

      {message && <div className={styles.deviceMessage}>{message}</div>}

      <div className={styles.officeAccessToolbar}>
        <div>
          <span>Office computers</span>
          <strong>{rows.length} registered</strong>
        </div>
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={onRefresh}
          disabled={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {sortedRows.length === 0 ? (
        <div className={styles.emptyState}>
          <strong>No computers registered for this office</strong>
          <span>
            Once a computer registers for {office}, it will appear here. Admin must fully approve it
            before a supervisor can allow Office Log Access.
          </span>
        </div>
      ) : (
        <div className={styles.officeAccessGrid}>
          {sortedRows.map((row) => {
            const adminApproved = row.approval_status === 'approved' && row.is_active === true;
            const hasAccess = adminApproved && row.office_log_access === true;

            return (
              <article key={row.id} className={styles.officeAccessCard}>
                <div className={styles.officeAccessCardTop}>
                  <div>
                    <h3>{row.device_name || 'Unnamed computer'}</h3>
                    <p>{row.requested_by_name || row.requested_by_email || 'Registration request'}</p>
                  </div>

                  <span
                    className={`${styles.officeAccessStatus} ${
                      hasAccess
                        ? styles.officeAccessStatusAllowed
                        : adminApproved
                          ? styles.officeAccessStatusReady
                          : styles.officeAccessStatusWaiting
                    }`}
                  >
                    {hasAccess ? 'Office Log Allowed' : adminApproved ? 'Admin Approved' : 'Waiting for Admin'}
                  </span>
                </div>

                <div className={styles.officeAccessMeta}>
                  <div>
                    <span>Admin Trust</span>
                    <strong>
                      {row.approval_status === 'approved'
                        ? row.is_active
                          ? 'Approved & Active'
                          : 'Approved but Disabled'
                        : row.approval_status === 'rejected'
                          ? 'Rejected'
                          : 'Pending'}
                    </strong>
                  </div>
                  <div>
                    <span>Office Log</span>
                    <strong>{hasAccess ? 'Allowed' : 'No Access'}</strong>
                  </div>
                  <div>
                    <span>Last Used</span>
                    <strong>{row.last_used_at ? formatDateTime(row.last_used_at) : 'Never'}</strong>
                  </div>
                  <div>
                    <span>Registered</span>
                    <strong>{formatDateTime(row.created_at)}</strong>
                  </div>
                </div>

                <div className={styles.officeAccessActions}>
                  {adminApproved ? (
                    hasAccess ? (
                      <button
                        type="button"
                        className={styles.dangerTextButton}
                        onClick={() => onToggleAccess(row, false)}
                      >
                        Remove Office Log Access
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={styles.primaryButton}
                        onClick={() => onToggleAccess(row, true)}
                      >
                        Allow Office Log
                      </button>
                    )
                  ) : (
                    <button type="button" className={styles.secondaryButton} disabled>
                      Admin Approval Required
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}


function DashboardStat({ label, value, note = '', tone = 'default' }) {
  return (
    <div className={`${styles.statCard} ${styles[`statCard_${tone}`] || ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {note && <small>{note}</small>}
    </div>
  );
}

function QuoteLifecycleBadge({ status }) {
  const labels = {
    completed: 'Completed',
    in_progress: 'In Progress',
    stale: 'Stale',
    unknown: 'Unknown',
  };

  return (
    <span className={`${styles.lifecycleBadge} ${styles[`lifecycle_${status}`] || styles.lifecycle_unknown}`}>
      {labels[status] || status}
    </span>
  );
}

function BridgeField({ label, value, mono = false }) {
  return (
    <div className={styles.bridgeField}>
      <span>{label}</span>
      <strong className={mono ? styles.mono : ''}>{value || '—'}</strong>
    </div>
  );
}

function EmptyState({ title, detail }) {
  return (
    <div className={styles.emptyState}>
      <strong>{title}</strong>
      <span>{detail}</span>
    </div>
  );
}